// src/components/BibleReader.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import Hls from "hls.js";
import {
  FaLayerGroup,
  FaBookReader,
  FaGripLines,
  FaStepBackward,
  FaStepForward,
  FaArrowLeft,
  FaArrowRight,
  FaPlay,
  FaPause,
  FaTimes,
  FaUndoAlt,
  FaTh,
} from "react-icons/fa";

import "./BibleReader.css"; // Import your CSS file here
import RandomYouTubePlayer from "./components/RandomYouTubePlayer";
import SEO from "./components/SEO";
import { BIBLE_BOOKS } from "./utils/bibleBooks";
import useTouchNavigation from "./utils/useTouchNavigation";

const booksData = BIBLE_BOOKS;

const BIBLEBRAIN_API_KEY = "0c8bc50c-f8e5-4dfe-a9d0-4786cd541c9a";
const NKJV_BIBLE_ID = "091debea1d2e8c3a-01";
const GLOBAL_WATERMARK_TEXT = "GlobalChristianCommunity.com";

// Try to fetch normally, then fall back to a CORS proxy if that fails
async function fetchJsonWithCors(url) {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (err) {
    try {
      const corsUrl = `https://cors.isomorphic-git.org/${url}`;
      const res = await fetch(corsUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (err2) {
      console.error("CORS fetch failed:", err2);
      throw err2;
    }
  }
}

const bibleBrainBookIds = {
  Genesis: "GEN",
  Exodus: "EXO",
  Leviticus: "LEV",
  Numbers: "NUM",
  Deuteronomy: "DEU",
  Joshua: "JOS",
  Judges: "JDG",
  Ruth: "RUT",
  "1 Samuel": "1SA",
  "2 Samuel": "2SA",
  "1 Kings": "1KI",
  "2 Kings": "2KI",
  "1 Chronicles": "1CH",
  "2 Chronicles": "2CH",
  Ezra: "EZR",
  Nehemiah: "NEH",
  Esther: "EST",
  Job: "JOB",
  Psalms: "PSA",
  Proverbs: "PRO",
  Ecclesiastes: "ECC",
  "Song of Solomon": "SNG",
  Isaiah: "ISA",
  Jeremiah: "JER",
  Lamentations: "LAM",
  Ezekiel: "EZK",
  Daniel: "DAN",
  Hosea: "HOS",
  Joel: "JOL",
  Amos: "AMO",
  Obadiah: "OBA",
  Jonah: "JON",
  Micah: "MIC",
  Nahum: "NAM",
  Habakkuk: "HAB",
  Zephaniah: "ZEP",
  Haggai: "HAG",
  Zechariah: "ZEC",
  Malachi: "MAL",
  Matthew: "MAT",
  Mark: "MRK",
  Luke: "LUK",
  John: "JHN",
  Acts: "ACT",
  Romans: "ROM",
  "1 Corinthians": "1CO",
  "2 Corinthians": "2CO",
  Galatians: "GAL",
  Ephesians: "EPH",
  Philippians: "PHP",
  Colossians: "COL",
  "1 Thessalonians": "1TH",
  "2 Thessalonians": "2TH",
  "1 Timothy": "1TI",
  "2 Timothy": "2TI",
  Titus: "TIT",
  Philemon: "PHM",
  Hebrews: "HEB",
  James: "JAS",
  "1 Peter": "1PE",
  "2 Peter": "2PE",
  "1 John": "1JN",
  "2 John": "2JN",
  "3 John": "3JN",
  Jude: "JUD",
  Revelation: "REV",
};

const BIBLE_IS_API_BASE = "https://4.dbt.io/api";
const BIBLE_IS_API_VERSION = "4";
const OLD_TESTAMENT_BOOK_COUNT = 39;
const DEFAULT_VERSION = "NKJV";
const DISPLAY_MODES = ["default", "slide", "full"];
const HIGHLIGHT_MODES = ["verse", "word", "none"];
const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "ta", label: "Tamil" },
  { value: "te", label: "Telugu" },
  { value: "hi", label: "Hindi" },
  { value: "ml", label: "Malayalam" },
];
const VERSION_OPTIONS = [
  { value: "KJV", label: "KJV" },
  { value: "NKJV", label: "NKJV" },
  { value: "NIV", label: "NIV" },
  { value: "ESV", label: "ESV" },
  { value: "WEB", label: "World Bible (WEB)" },
  { value: "RSV", label: "RSV (Catholic)" },
  { value: "ICB", label: "ICB (Kids)" },
  { value: "AMP", label: "Amplified (Study)" },
  { value: "CSB", label: "CSB" },
  { value: "NLT", label: "NLT" },
];
const SESSION_NAVIGATION_MESSAGE = "gcc-session-navigation";
const SESSION_NAVIGATION_RESPONSE = "gcc-session-navigation-response";
const SESSION_FOCUS_MESSAGE = "gcc-session-focus";

const BIBLE_IS_FILESETS = {
  en: {
    text: {
      KJV: { ot: "ENGKJVO_ET", nt: "ENGKJVN_ET" },
      NKJV: { ot: "ENGNKJO_ET", nt: "ENGNKJN_ET" },
      NIV: null,
      ESV: { ot: "ENGESVO_ET", nt: "ENGESVN_ET" },
      WEB: { ot: "ENGWEBO_ET", nt: "ENGWEBN_ET" },
      default: { ot: "ENGKJVO_ET", nt: "ENGKJVN_ET" },
    },
    audio: {
      KJV: { ot: "ENGKJVO1DA", nt: "ENGKJVN1DA" },
      NKJV: { ot: "ENGNKJO1DA", nt: "ENGNKJN1DA" },
      NIV: null,
      ESV: { ot: "ENGESVO1DA", nt: "ENGESVN1DA" },
      WEB: null,
      default: { ot: "ENGKJVO1DA", nt: "ENGKJVN1DA" },
    },
    audioDrama: {
      KJV: { ot: "ENGKJVO2DA", nt: "ENGKJVN2DA" },
      NKJV: null,
      NIV: null,
      ESV: { ot: "ENGESVO2DA", nt: "ENGESVN2DA" },
      WEB: { ot: null, nt: "ENGWEBN2DA" },
      default: { ot: "ENGKJVO2DA", nt: "ENGKJVN2DA" },
    },
    video: {
      KJV: { any: "ENGKJVP2DV" },
      NIV: { any: "ENGNIVP2DV" },
      ESV: { any: "ENGESHP2DV" },
      default: { any: "ENGKJVP2DV" },
    },
  },
  ta: {
    text: {
      default: { ot: "TAMDPIO_ET", nt: "TAMDPIN_ET" },
    },
    audio: {
      default: { ot: "TAMDPIO1DA", nt: "TAMDPIN1DA" },
    },
    video: {
      default: { any: "TAMLPFP2DV" },
    },
  },
  te: {
    text: {
      default: { ot: "TCWWTCO_ET", nt: "TCWWTCN_ET" },
    },
    audio: {
      default: { ot: null, nt: "TCWWTCN1DA" },
    },
    audioDrama: {
      default: { ot: null, nt: "TCWWTCN2DA" },
    },
  },
  hi: {
    text: {
      default: { ot: "HINDPIO_ET", nt: "HINBCSN_ET" },
    },
    audio: {
      default: { ot: "HINDPIO1DA", nt: "HINBCSN1DA" },
    },
  },
  ml: {
    text: {
      default: { ot: "MALDPIO_ET", nt: "MALDPIN_ET" },
    },
    audio: {
      default: { ot: "MALDPIO1DA", nt: "MALDPIN1DA" },
    },
    video: {
      default: { any: "MALBIBP2DV" },
    },
  },
};

function getTestamentKey(bookName) {
  const idx = booksData.findIndex((b) => b.name === bookName);
  if (idx === -1) return "ot";
  return idx < OLD_TESTAMENT_BOOK_COUNT ? "ot" : "nt";
}

function resolveBibleIsFileset(language, version, testamentKey, mediaType) {
  const langKey = (language || "en").toLowerCase();
  const languageMappings =
    BIBLE_IS_FILESETS[langKey] || BIBLE_IS_FILESETS.en || {};
  const mediaMappings = languageMappings[mediaType];
  const fallbackMediaMappings =
    langKey === "en" || mediaType !== "text"
      ? null
      : (BIBLE_IS_FILESETS.en && BIBLE_IS_FILESETS.en[mediaType]) || null;

  function pickMapping(mappings) {
    if (!mappings) return undefined;
    if (
      Object.prototype.hasOwnProperty.call(mappings, version) &&
      mappings[version] === null
    ) {
      return null;
    }
    const versionEntry = mappings[version];
    if (versionEntry) {
      return versionEntry;
    }
    return mappings.default;
  }

  function extractFilesetId(entry) {
    if (!entry) return undefined;
    if (typeof entry === "string") return entry;
    if (
      typeof entry === "object" &&
      entry &&
      Object.prototype.hasOwnProperty.call(entry, testamentKey)
    ) {
      return entry[testamentKey];
    }
    if (typeof entry === "object" && entry && entry.any) {
      return entry.any;
    }
    return undefined;
  }

  const selected = pickMapping(mediaMappings);
  if (selected === null) return null;
  let filesetId = extractFilesetId(selected);
  if (!filesetId && mediaMappings?.default) {
    filesetId = extractFilesetId(mediaMappings.default);
  }

  if (!filesetId && fallbackMediaMappings) {
    const fallbackSelected = pickMapping(fallbackMediaMappings);
    if (fallbackSelected && fallbackSelected !== null) {
      filesetId = extractFilesetId(fallbackSelected);
      if (!filesetId && fallbackMediaMappings.default) {
        filesetId = extractFilesetId(fallbackMediaMappings.default);
      }
    }
  }

  return filesetId || null;
}

function ensureBibleIsAuthParams(url) {
  if (!url) return url;
  try {
    const parsed = new URL(url);
    const shouldAppend =
      parsed.hostname && parsed.hostname.toLowerCase().endsWith("4.dbt.io");
    if (!shouldAppend) {
      return url;
    }
    if (!parsed.searchParams.has("key")) {
      parsed.searchParams.set("key", BIBLEBRAIN_API_KEY);
    }
    if (!parsed.searchParams.has("v")) {
      parsed.searchParams.set("v", BIBLE_IS_API_VERSION);
    }
    return parsed.toString();
  } catch (err) {
    const shouldAppend = /^https?:\/\/[^/]*4\.dbt\.io/i.test(url);
    if (!shouldAppend) return url;
    let finalUrl = url;
    let separator = url.includes("?") ? "&" : "?";
    if (!url.includes("key=")) {
      finalUrl += `${separator}key=${BIBLEBRAIN_API_KEY}`;
      separator = "&";
    }
    if (!url.includes("v=")) {
      finalUrl += `${separator}v=${BIBLE_IS_API_VERSION}`;
    }
    return finalUrl;
  }
}

function parseCustomSequence(raw) {
  if (!raw) return [];
  return raw
    .split(",")
    .map((segment) => {
      const trimmed = (segment || "").trim();
      if (!trimmed) return null;
      const tokens = trimmed.split(/\s+/);
      if (tokens.length === 0) return null;
      let bestMatch = null;

      booksData.forEach((book, index) => {
        const bookTokens = book.name.split(/\s+/);
        if (tokens.length < bookTokens.length) return;
        const matches = bookTokens.every((bookToken, idx) =>
          bookToken.toLowerCase().startsWith(tokens[idx].toLowerCase()),
        );
        if (!matches) return;
        if (!bestMatch || bookTokens.length > bestMatch.tokenCount) {
          bestMatch = { book, index, tokenCount: bookTokens.length };
        }
      });

      if (!bestMatch) return null;
      const remainderRaw = tokens.slice(bestMatch.tokenCount).join(" ");
      if (!remainderRaw) return null;
      const normalized = remainderRaw
        .replace(/\bto\b/gi, " ")
        .replace(/:/g, " ")
        .replace(/\s*-\s*/g, "-")
        .replace(/\s+/g, " ")
        .trim();
      if (!normalized) return null;

      const chapterMatch = normalized.match(/^(\d+)/);
      if (!chapterMatch) return null;
      const chapter = parseInt(chapterMatch[1], 10);
      if (
        Number.isNaN(chapter) ||
        chapter < 1 ||
        chapter > (bestMatch.book.chapters ?? 1)
      ) {
        return null;
      }

      let remainder = normalized.slice(chapterMatch[0].length).trim();
      if (remainder.startsWith(":")) remainder = remainder.slice(1).trim();

      let verses = "";
      if (remainder) {
        const verseMatch = remainder.match(/(\d+)(?:\s*-\s*(\d+))?/);
        if (verseMatch) {
          const start = parseInt(verseMatch[1], 10);
          const end = verseMatch[2] ? parseInt(verseMatch[2], 10) : null;
          if (!Number.isNaN(start)) {
            verses =
              end && !Number.isNaN(end) ? `${start}-${end}` : `${start}`;
          }
        }
      }

      return {
        bookIndex: bestMatch.index,
        book: bestMatch.book.name,
        chapter,
        verses,
      };
    })
    .filter(Boolean);
}

function findCustomSequenceIndex(entries, target) {
  if (!Array.isArray(entries) || entries.length === 0 || !target) return 0;
  const idx = entries.findIndex(
    (entry) =>
      entry.bookIndex === target.bookIndex &&
      entry.chapter === target.chapter &&
      String(entry.verses || "") === String(target.verses || ""),
  );
  return idx >= 0 ? idx : 0;
}

function parseURLParams(search) {
  const params = new URLSearchParams(
    search && search.startsWith("?") ? search.substring(1) : search,
  );

  let bookIndex = 0;
  let chapter = 1;
  let version = DEFAULT_VERSION;
  let language = "en";
  let page = 0;
  let lines = 3;
  let wpm = 170;
  let verses = "";
  let mode = "default";
  let secondary = false;
  let secondaryLang = "en";
  let secondaryVersion = DEFAULT_VERSION;
  let customRaw = "";
  let customEntries = [];
  let autoNext = false;

  const bookParam = params.get("book");
  const hasBookParam = params.has("book");
  if (bookParam !== null) {
    const idx = parseInt(bookParam, 10);
    if (!isNaN(idx) && idx >= 0 && idx < booksData.length) {
      bookIndex = idx;
    } else {
      const found = booksData.findIndex(
        (b) => b.name.toLowerCase() === bookParam.toLowerCase(),
      );
      if (found !== -1) bookIndex = found;
    }
  }

  const chapterParam = params.get("chapter");
  const hasChapterParam = params.has("chapter");
  if (chapterParam !== null) {
    const c = parseInt(chapterParam, 10);
    if (!isNaN(c)) chapter = c;
  }

  const versionParam = params.get("version");
  if (versionParam !== null) version = versionParam;

  const languageParam = params.get("language");
  if (languageParam !== null) language = languageParam;

  const pageParam = params.get("page");
  if (pageParam !== null) {
    const p = parseInt(pageParam, 10);
    if (!isNaN(p) && p >= 0) page = p;
  }

  const linesParam = params.get("lines");
  if (linesParam !== null) {
    const l = parseInt(linesParam, 10);
    if (!isNaN(l) && l >= 1 && l <= 20) lines = l;
  }

  const wpmParam = params.get("wpm");
  if (wpmParam !== null) {
    const w = parseInt(wpmParam, 10);
    if (!isNaN(w) && w >= 1) wpm = w;
  }

  const versesParam = params.get("verses");
  const verseParam = params.get("verse");
  const rangeParam = params.get("range");
  const hasVerseParam = params.has("verses") || params.has("verse") || params.has("range");
  if (versesParam !== null && versesParam !== "") {
    verses = versesParam;
  } else if (rangeParam !== null && rangeParam !== "") {
    verses = rangeParam;
  } else if (verseParam !== null && verseParam !== "") {
    verses = verseParam;
  }

  const modeParam = params.get("mode");
  if (modeParam !== null) mode = modeParam;

  const secondaryParam = params.get("secondary");
  if (secondaryParam !== null) secondary = secondaryParam === "1";

  const secondaryLangParam = params.get("secondaryLang");
  if (secondaryLangParam !== null) secondaryLang = secondaryLangParam;

  const secondaryVersionParam = params.get("secondaryVersion");
  if (secondaryVersionParam !== null) secondaryVersion = secondaryVersionParam;

  const customParam = params.get("custom");
  if (customParam !== null) {
    customRaw = customParam;
    customEntries = parseCustomSequence(customParam);
    if (customEntries.length > 0 && !hasBookParam && !hasChapterParam && !hasVerseParam) {
      const first = customEntries[0];
      bookIndex = first.bookIndex;
      chapter = first.chapter;
      verses = first.verses || "";
    }
  }

  const autoNextParam = params.get("autonext");
  if (autoNextParam !== null) {
    autoNext = autoNextParam === "1" || autoNextParam === "true" || autoNextParam === "yes";
  }

  return {
    bookIndex,
    chapter,
    version,
    language,
    page,
    lines,
    wpm,
    verses,
    mode,
    secondary,
    secondaryLang,
    secondaryVersion,
    customRaw,
    customEntries,
    autoNext,
  };
}

const initialParams =
  typeof window !== "undefined" ? parseURLParams(window.location.search) : {};
const shouldShowLandingForSearch = (search) => {
  if (!search) return true;
  const params = new URLSearchParams(search);
  if (params.size === 0) return true;
  if (params.size === 1 && params.has("domain")) return true;
  return false;
};

const determineInitialLandingVisible = () => {
  if (typeof window === "undefined") return false;
  const search = window.location.search || "";
  return shouldShowLandingForSearch(search);
};

const initialLandingVisible = determineInitialLandingVisible();

function parseLandingQuickInput(raw) {
  if (!raw) return { key: "", chapter: null };
  const sanitized = raw.replace(/\s+/g, "");
  if (!sanitized) return { key: "", chapter: null };
  const bookMatch = sanitized.match(/^([0-9]?[A-Za-z]+)/);
  const bookKey = bookMatch
    ? bookMatch[1].toLowerCase().replace(/[^a-z0-9]/g, "")
    : "";
  const remainder = bookMatch ? sanitized.slice(bookMatch[1].length) : "";
  const chapterMatch = remainder.match(/^(\d+)/);
  const chapter =
    chapterMatch && chapterMatch[1]
      ? parseInt(chapterMatch[1], 10)
      : null;
  return { key: bookKey, chapter: Number.isNaN(chapter) ? null : chapter };
}

function BibleReaderMain({ source = "classic" } = {}) {
  const isBibleIs = source === "bibleis";
  const parentOriginRef = useRef("*");
  const isEmbeddedRef = useRef(false);
  // -----------------------------
  //           State
  // -----------------------------
  const [books] = useState(booksData);
  const sanitizedBooks = useMemo(
    () =>
      books.map((b, idx) => ({
        index: idx,
        name: b.name,
        key: b.name.toLowerCase().replace(/[^a-z0-9]/g, ""),
      })),
    [books],
  );
  const oldTestamentBooks = useMemo(
    () => books.slice(0, OLD_TESTAMENT_BOOK_COUNT),
    [books],
  );
  const newTestamentBooks = useMemo(
    () => books.slice(OLD_TESTAMENT_BOOK_COUNT),
    [books],
  );

  function extractBookLookup(raw) {
    if (!raw) return { key: "", letters: "", chapter: null };
    const match = raw.match(/^([0-9]?)([A-Za-z]+)/);
    if (!match) return { key: "", letters: "", chapter: null };
    const numericPart = match[1] ? parseInt(match[1], 10) : null;
    const lettersPart = match[2] || "";
    const combined = `${match[1] ?? ""}${lettersPart}`;
    const key = combined.toLowerCase().replace(/[^a-z0-9]/g, "");
    return {
      key,
      letters: lettersPart,
      chapter:
        numericPart !== null && !Number.isNaN(numericPart) ? numericPart : null,
    };
  }
  const [currentBookIndex, setCurrentBookIndex] = useState(
    initialParams.bookIndex ?? 0,
  );
  const [currentBook, setCurrentBook] = useState(
    booksData[initialParams.bookIndex ?? 0].name,
  );
  const [currentChapter, setCurrentChapter] = useState(
    initialParams.chapter ?? 1,
  );
  const [currentVerses, setCurrentVerses] = useState([]);
  const [currentVersePage, setCurrentVersePage] = useState(
    initialParams.page ?? 0,
  );
  const getDefaultLinesPerPage = () => initialParams.lines || 3;
  const [versesPerPage, setVersesPerPage] = useState(getDefaultLinesPerPage);
  const [currentVerseRange, setCurrentVerseRange] = useState(
    initialParams.verses ?? "",
  );
  const [customSequenceInput, setCustomSequenceInput] = useState(
    initialParams.customRaw ?? "",
  );
  const [customSequenceEntries, setCustomSequenceEntries] = useState(
    initialParams.customEntries ?? [],
  );
  const [customSequenceIndex, setCustomSequenceIndex] = useState(
    initialParams.customEntries?.length
      ? findCustomSequenceIndex(initialParams.customEntries, {
          bookIndex: initialParams.bookIndex,
          chapter: initialParams.chapter,
          verses: initialParams.verses,
        })
      : 0,
  );
  const [currentVersion, setCurrentVersion] = useState(
    initialParams.version ?? DEFAULT_VERSION,
  );
  const [currentLanguage, setCurrentLanguage] = useState(
    initialParams.language ?? "en",
  );
  const [fetchError, setFetchError] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [wordsPerMinute, setWordsPerMinute] = useState(
    initialParams.wpm ?? 170,
  );
  const [speechRate, setSpeechRate] = useState(1);
  const isCustomSequenceActive = customSequenceEntries.length > 0;
  const computeResponsiveFontSize = () => {
    if (typeof window === "undefined") return 34;
    const { innerWidth: w } = window;
    if (w <= 640) return 25;
    if (w >= 1280) return 40;
    const ratio = (w - 640) / (1280 - 640);
    return 25 + ratio * (40 - 25);
  };
  const [fontSize, setFontSize] = useState(() => computeResponsiveFontSize());
  const manualFontOverrideRef = useRef(false);
  const [mobileFontScale, setMobileFontScale] = useState(1);
  const [highlightColor, setHighlightColor] = useState("#1a1a1a");
  const [highlightMode, setHighlightMode] = useState("none");
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  useEffect(() => {
    if (!isBibleIs || typeof document === "undefined" || !document.body) return undefined;
    document.body.classList.add("worship-fullscreen");
    return () => {
      document.body.classList.remove("worship-fullscreen");
    };
  }, [isBibleIs]);
  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const embedded = window.parent && window.parent !== window;
    isEmbeddedRef.current = Boolean(embedded);
    if (!embedded) {
      return undefined;
    }
    if (typeof document !== "undefined" && document.referrer) {
      try {
        const referrerOrigin = new URL(document.referrer).origin;
        if (referrerOrigin) {
          parentOriginRef.current = referrerOrigin;
        }
      } catch (error) {
        parentOriginRef.current = "*";
      }
    }
    return undefined;
  }, []);
  const sendSessionFocusRequest = useCallback((target) => {
    if (!isEmbeddedRef.current || typeof window === "undefined") {
      return;
    }
    const targetOrigin = parentOriginRef.current || "*";
    try {
      window.parent.postMessage(
        {
          type: SESSION_FOCUS_MESSAGE,
          payload: { target },
        },
        targetOrigin,
      );
    } catch (error) {
      // ignore
    }
  }, []);
  const sendSessionNavigationResponse = useCallback((requestId, action, handled) => {
    if (!isEmbeddedRef.current || typeof window === "undefined") {
      return;
    }
    const targetOrigin = parentOriginRef.current || "*";
    try {
      window.parent.postMessage(
        {
          type: SESSION_NAVIGATION_RESPONSE,
          payload: { requestId, action, handled },
        },
        targetOrigin,
      );
    } catch (error) {
      // Ignore cross-origin issues
    }
  }, []);
  const [showLandingView, setShowLandingView] = useState(initialLandingVisible);
  const [landingChapterSelections, setLandingChapterSelections] = useState(
    () => booksData.map((book) => Math.max(book.chapters || 1, 1)),
  );
  const [landingQuickBuffer, setLandingQuickBuffer] = useState("");
  const landingQuickBufferRef = useRef("");
  const [landingHighlightedBookIndex, setLandingHighlightedBookIndex] =
    useState(null);
  const [displayMode, setDisplayMode] = useState(
    initialParams.mode ?? "default",
  );
  const [secondaryEnabled, setSecondaryEnabled] = useState(
    initialParams.secondary ?? false,
  );
  const [secondaryLanguage, setSecondaryLanguage] = useState(
    initialParams.secondaryLang ?? initialParams.language ?? "en",
  );
  const [secondaryVersion, setSecondaryVersion] = useState(
    initialParams.secondaryVersion ?? DEFAULT_VERSION,
  );
  const [secondaryVerses, setSecondaryVerses] = useState([]);
  const [secondaryError, setSecondaryError] = useState("");
  const [quickJumpBuffer, setQuickJumpBuffer] = useState("");
  const quickJumpBufferRef = useRef("");
  useEffect(() => {
    quickJumpBufferRef.current = quickJumpBuffer;
  }, [quickJumpBuffer]);
  useEffect(() => {
    landingQuickBufferRef.current = landingQuickBuffer;
  }, [landingQuickBuffer]);
  const searchInputRef = useRef(null);
  const contentBoxRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(
    () => (typeof window !== "undefined" ? window.innerWidth : 1024),
  );
  const fontSliderMax = useMemo(() => {
    if (viewportWidth <= 640) return 64;
    if (viewportWidth >= 1600) return 120;
    return 90;
  }, [viewportWidth]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  useEffect(() => {
    if (manualFontOverrideRef.current) return;
    setFontSize(computeResponsiveFontSize());
  }, [viewportWidth]);

  const adjustMobileFontScale = useCallback(() => {
    if (!contentBoxRef.current) return;
    const container = contentBoxRef.current;
    const isMobile = viewportWidth <= 768;
    const applyScale = (scale) => {
      container.style.setProperty("--br-mobile-font-scale", scale.toString());
    };
    if (!isMobile) {
      applyScale(1);
      if (mobileFontScale !== 1) {
        setMobileFontScale(1);
      }
      return;
    }
    let scale = Math.min(mobileFontScale, 0.3);
    const minScale = 0.12;
    const step = 0.02;
    const maxIterations = 40;
    applyScale(scale);
    let iterations = 0;
    while (container.scrollHeight > container.clientHeight && scale > minScale && iterations < maxIterations) {
      scale = Math.max(minScale, parseFloat((scale - step).toFixed(2)));
      applyScale(scale);
      iterations += 1;
    }
    if (scale !== mobileFontScale) {
      setMobileFontScale(scale);
    }
  }, [mobileFontScale, viewportWidth]);

  useLayoutEffect(() => {
    adjustMobileFontScale();
  }, [
    adjustMobileFontScale,
    currentVerses,
    currentVersePage,
    displayMode,
    fontSize,
    versesPerPage,
    showLandingView,
    secondaryEnabled,
    secondaryVerses,
  ]);
  const languageSelectorRef = useRef(null);
  const versionSelectorRef = useRef(null);
  const bookSelectorRef = useRef(null);
  const chapterSelectorRef = useRef(null);
  const landingSelectionState = useRef({
    prefix: "",
    cursor: 0,
    matches: [],
  });
  const topControlsHiddenRef = useRef(true);
  const updateQuickJumpBuffer = useCallback((value) => {
    quickJumpBufferRef.current = value;
    setQuickJumpBuffer(value);
  }, []);

  // Voice selection state
  const [selectedVoice, setSelectedVoice] = useState("Default");
  const [availableVoices, setAvailableVoices] = useState([]);
  const [bibleIsMediaType, setBibleIsMediaType] = useState("text");
  const [audioData, setAudioData] = useState(null);
  const [audioIsLoading, setAudioIsLoading] = useState(false);
  const [audioError, setAudioError] = useState("");
  const [audioIsPlaying, setAudioIsPlaying] = useState(false);
  const [audioFilesetId, setAudioFilesetId] = useState("");
  const [audioTimestamps, setAudioTimestamps] = useState([]);
  const [currentAudioVerse, setCurrentAudioVerse] = useState(null);
  const audioAutoAdvanceRef = useRef(false);
  const audioPendingAutoPlayRef = useRef(false);
  const videoAutoAdvanceRef = useRef(false);
  const videoPendingAutoPlayRef = useRef(false);
  const [autoPlayNext, setAutoPlayNext] = useState(Boolean(initialParams.autoNext));
  const [videoData, setVideoData] = useState(null);
  const [videoError, setVideoError] = useState("");
  const [videoIsLoading, setVideoIsLoading] = useState(false);
  const [videoIsPlaying, setVideoIsPlaying] = useState(false);
  const [videoVerseTimings, setVideoVerseTimings] = useState([]);
  const [videoMetadataTick, setVideoMetadataTick] = useState(0);

  const seoConfig = isBibleIs
      ? {
        title: "Bible.is Reader - Global Christian Community",
        description:
          "Read, listen, and watch Bible.is video chapters with simple controls.",
        keywords:
          "Bible.is, Bible video, Bible reader, Scripture, Bible study, Global Christian Community",
        url: "https://globalchristiancommunity.com/bibleis",
      }
    : {
        title: "Bible Reader - Global Christian Community",
        description:
          "Select a book and chapter to read or listen to Scripture.",
        keywords:
          "Bible reader, Bible study, Bible, Christian, Global Christian Community",
        url: "https://globalchristiancommunity.com/bible",
      };

  const location = useLocation();
  useEffect(() => {
    const desired = shouldShowLandingForSearch(location.search || "");
    setShowLandingView((prev) => (prev === desired ? prev : desired));
  }, [location.search]);

  function applyVerseParams(params, verses) {
    if (!verses) return;
    const firstSegment = verses.split(",")[0]?.trim();
    if (!firstSegment) return;
    const [startStr, endStrRaw] = firstSegment.split("-");
    const start = parseInt(startStr, 10);
    if (isNaN(start)) return;
    params.set("verse_start", String(start));
    const end = parseInt(endStrRaw ?? "", 10);
    params.set("verse_end", String(!isNaN(end) ? end : start));
  }

  useEffect(() => {
    if (!customSequenceEntries.length) return;
    const maxIndex = customSequenceEntries.length - 1;
    if (customSequenceIndex > maxIndex) {
      setCustomSequenceIndex(maxIndex);
      return;
    }
    const entry = customSequenceEntries[customSequenceIndex];
    if (!entry) return;
    const nextVerses = entry.verses || "";
    setCurrentBookIndex((prev) =>
      prev === entry.bookIndex ? prev : entry.bookIndex,
    );
    setCurrentBook((prev) => (prev === entry.book ? prev : entry.book));
    setCurrentChapter((prev) => (prev === entry.chapter ? prev : entry.chapter));
    setCurrentVerseRange((prev) => (prev === nextVerses ? prev : nextVerses));
    setCurrentVersePage(0);
  }, [customSequenceEntries, customSequenceIndex]);

  function resetAudioElement() {
    const audioEl = audioRef.current;
    if (!audioEl) return;
    audioEl.pause();
    audioEl.removeAttribute("src");
    audioEl.load();
    setAudioIsPlaying(false);
    audioAutoAdvanceRef.current = false;
    audioPendingAutoPlayRef.current = false;
  }

  function resetVideoElement() {
    if (hlsInstanceRef.current) {
      hlsInstanceRef.current.destroy();
      hlsInstanceRef.current = null;
    }
    const videoEl = videoRef.current;
    if (!videoEl) return;
    videoEl.pause();
    videoEl.removeAttribute("src");
    videoEl.load();
    setVideoIsPlaying(false);
    videoPendingAutoPlayRef.current = false;
  }

  function formatSeconds(totalSeconds) {
    if (
      totalSeconds === null ||
      totalSeconds === undefined ||
      Number.isNaN(totalSeconds)
    ) {
      return "";
    }
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.round(totalSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }

  const [searchQuery, setSearchQuery] = useState("");
  const [autocompleteItems, setAutocompleteItems] = useState([]);
  const [topControlsHidden, setTopControlsHidden] = useState(true);
  useEffect(() => {
    topControlsHiddenRef.current = topControlsHidden;
  }, [topControlsHidden]);
  const bottomControlsHiddenRef = useRef(false);
  const [bottomControlsHidden, setBottomControlsHidden] = useState(false);
  useEffect(() => {
    bottomControlsHiddenRef.current = bottomControlsHidden;
  }, [bottomControlsHidden]);
  const navRef = useRef(null);
  const mediaPanelRef = useRef(null);
  const navDragOffsetRef = useRef({ x: 0, y: 0 });
  const [navPosition, setNavPosition] = useState({ left: null, top: null });
  const [isDraggingNav, setIsDraggingNav] = useState(false);

  // For the help modal
  const [showHelp, setShowHelp] = useState(false);

  // For share tooltip
  const [showShareTooltip, setShowShareTooltip] = useState(false);

  // Timeout reference for auto-advance
  const autoAdvanceRef = useRef(null);

  // Audio ref (if you actually implement audio)
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const hlsInstanceRef = useRef(null);
  const verseTouchTargetRef = useRef(null);

  // Tokens and highlighting state for TTS
  const [slideTokens, setSlideTokens] = useState([]);
  const [currentTokenIndex, setCurrentTokenIndex] = useState(0);

  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl) return;
    const handlePlay = () => {
      setAudioIsPlaying(true);
      audioAutoAdvanceRef.current = autoPlayNext;
    };
    const handlePause = () => {
      setAudioIsPlaying(false);
      audioAutoAdvanceRef.current = false;
    };
    const handleEnded = () => {
      setAudioIsPlaying(false);
      setCurrentAudioVerse(null);
      const shouldAdvance = audioAutoAdvanceRef.current;
      if (shouldAdvance) {
        moveToNextChapter(true);
      }
      audioAutoAdvanceRef.current = false;
    };
    const handleTimeUpdate = () => {
      if (
        !isBibleIs ||
        (bibleIsMediaType !== "audio" && bibleIsMediaType !== "audioDrama")
      ) {
        return;
      }
      if (!audioTimestamps.length) return;
      if (audioEl.paused) return;
      const currentTime = audioEl.currentTime;
      const tolerance = 0.25;
      let activeEntry = null;
      for (let i = 0; i < audioTimestamps.length; i += 1) {
        const entry = audioTimestamps[i];
        if (currentTime + tolerance >= entry.timestamp) {
          activeEntry = entry;
        } else {
          break;
        }
      }
      if (!activeEntry) {
        activeEntry = audioTimestamps[0];
      }
      if (!activeEntry) return;
      setCurrentAudioVerse((prev) =>
        prev === activeEntry.verse ? prev : activeEntry.verse,
      );
      if (highlightMode === "word" && displayMode !== "full") {
        const versesToShow = getDisplayedVerses();
        const pageOffset = currentVersePage * versesPerPage;
        const verseIndexOnPage = currentVerses.findIndex(
          (v) => v.verse === activeEntry.verse,
        );
        if (verseIndexOnPage !== -1) {
          const relativeIndex = verseIndexOnPage - pageOffset;
          if (relativeIndex >= 0 && relativeIndex < versesToShow.length) {
            const verseText = versesToShow[relativeIndex].text || "";
            const words = verseText.split(/\\s+/);
            const timestampIndex = audioTimestamps.findIndex(
              (t) => t.verse === activeEntry.verse,
            );
            const nextTimestamp =
              timestampIndex !== -1 && audioTimestamps[timestampIndex + 1]
                ? audioTimestamps[timestampIndex + 1].timestamp
                : null;
            let wordIndex = 0;
            if (nextTimestamp !== null) {
              const wordsPerSegment = Math.max(
                1,
                Math.round(((nextTimestamp - activeEntry.timestamp) * 3) /
                  Math.max(wordsPerMinute / 60, 1)),
              );
              wordIndex = wordsPerSegment - 1;
            }
            const cumulativeWords = [];
            versesToShow.forEach((verse, idx) => {
              const verseWords = verse.text.split(/\\s+/);
              verseWords.forEach((word) => cumulativeWords.push(word));
              if (idx === relativeIndex) {
                const flatIndex = cumulativeWords.length - words.length;
                const targetIndex = Math.max(0, flatIndex + wordIndex);
                setCurrentTokenIndex(
                  Math.min(targetIndex, cumulativeWords.length - 1),
                );
              }
            });
          }
        }
      }
      const verseIndex = currentVerses.findIndex(
        (v) => v.verse === activeEntry.verse,
      );
      if (verseIndex !== -1) {
        const targetPage = Math.floor(verseIndex / versesPerPage);
        if (displayMode !== "full") {
          setCurrentVersePage((prev) =>
            prev === targetPage ? prev : targetPage,
          );
        }
      }
    };
    audioEl.addEventListener("play", handlePlay);
    audioEl.addEventListener("pause", handlePause);
    audioEl.addEventListener("ended", handleEnded);
    audioEl.addEventListener("timeupdate", handleTimeUpdate);
    return () => {
      audioEl.removeEventListener("play", handlePlay);
      audioEl.removeEventListener("pause", handlePause);
      audioEl.removeEventListener("ended", handleEnded);
      audioEl.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [
    autoPlayNext,
    isBibleIs,
    bibleIsMediaType,
    audioTimestamps,
    currentVerses,
    versesPerPage,
    displayMode,
  ]);

  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl) return;
    audioEl.playbackRate = speechRate;
  }, [speechRate]);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return undefined;
    const handlePlay = () => {
      setVideoIsPlaying(true);
      videoAutoAdvanceRef.current = autoPlayNext;
    };
    const handlePause = () => {
      setVideoIsPlaying(false);
      videoAutoAdvanceRef.current = false;
    };
    const handleEnded = () => {
      setVideoIsPlaying(false);
      if (videoAutoAdvanceRef.current) {
        moveToNextChapter(true);
      }
      videoAutoAdvanceRef.current = false;
    };
    const handleLoadedMetadata = () =>
      setVideoMetadataTick((tick) => tick + 1);
    const handleTimeUpdate = () => {
      if (
        !isBibleIs ||
        bibleIsMediaType !== "video" ||
        !videoVerseTimings.length
      ) {
        return;
      }
      const currentTime = videoEl.currentTime;
      let activeVerse = null;
      for (let i = 0; i < videoVerseTimings.length; i += 1) {
        const currentTiming = videoVerseTimings[i];
        const nextTiming = videoVerseTimings[i + 1];
        if (
          currentTime >= currentTiming.startTime &&
          (!nextTiming || currentTime < nextTiming.startTime)
        ) {
          activeVerse = currentTiming.verse;
          break;
        }
      }
      if (!activeVerse && videoVerseTimings.length) {
        activeVerse = videoVerseTimings[videoVerseTimings.length - 1].verse;
      }
      if (!activeVerse) return;
      setCurrentAudioVerse((prev) => (prev === activeVerse ? prev : activeVerse));
      const verseIndex = currentVerses.findIndex(
        (verse) => verse.verse === activeVerse,
      );
      if (verseIndex !== -1 && displayMode !== "full") {
        const targetPage = Math.floor(verseIndex / versesPerPage);
        setCurrentVersePage((prev) => (prev === targetPage ? prev : targetPage));
      }
    };
    videoEl.addEventListener("play", handlePlay);
    videoEl.addEventListener("pause", handlePause);
    videoEl.addEventListener("ended", handleEnded);
    videoEl.addEventListener("loadedmetadata", handleLoadedMetadata);
    videoEl.addEventListener("timeupdate", handleTimeUpdate);
    return () => {
      videoEl.removeEventListener("play", handlePlay);
    videoEl.removeEventListener("pause", handlePause);
    videoEl.removeEventListener("ended", handleEnded);
    videoEl.removeEventListener("loadedmetadata", handleLoadedMetadata);
    videoEl.removeEventListener("timeupdate", handleTimeUpdate);
  };
  }, [
    autoPlayNext,
    bibleIsMediaType,
    currentVerses,
    displayMode,
    isBibleIs,
    videoVerseTimings,
    versesPerPage,
  ]);

  useEffect(() => {
    if (audioIsPlaying) {
      audioAutoAdvanceRef.current = autoPlayNext;
    }
    if (videoIsPlaying) {
      videoAutoAdvanceRef.current = autoPlayNext;
    }
  }, [autoPlayNext, audioIsPlaying, videoIsPlaying]);
  const utteranceRef = useRef(null);
  const isPlayingRef = useRef(false);

  // Refs so global key handler always has latest navigation functions
  const handleNextRef = useRef(() => {});
  const handlePrevRef = useRef(() => {});
  const moveNextChapterRef = useRef(() => {});
  const movePrevChapterRef = useRef(() => {});

  // -----------------------------
  //        Effects
  // -----------------------------

  /**
   * On component mount, we initialize from URL params if present,
   * otherwise fetch the default (Genesis 1).
   */
  useEffect(() => {
    handleURLParameters();
    // Add keydown listener
    const handleKeyDown = (event) => {
      const ctrlOrMeta = event.ctrlKey || event.metaKey;
      const altKey = event.altKey;
      const shiftKey = event.shiftKey;
      const activeElement = document.activeElement;
      const activeTag = activeElement?.tagName;
      const isRangeInput =
        activeTag === "INPUT" &&
        activeElement &&
        activeElement.getAttribute("type")?.toLowerCase?.() === "range";
      const isFormField =
        activeElement &&
        ["INPUT", "TEXTAREA", "SELECT"].includes(activeTag) &&
        !isRangeInput;
      if (event.key === "Tab" && shiftKey && !ctrlOrMeta && !altKey && !event.metaKey) {
        event.preventDefault();
        sendSessionFocusRequest("controls");
        return;
      }

      if (event.key === "F1") {
        event.preventDefault();
        openHelpModal();
        return;
      }

      if (showHelp && event.key === "Escape" && !ctrlOrMeta && !altKey && !shiftKey) {
        event.preventDefault();
        closeHelpModal();
        return;
      }

      if (
        isBibleIs &&
        event.key === "Alt" &&
        !ctrlOrMeta &&
        !shiftKey &&
        !event.metaKey
      ) {
        event.preventDefault();
        if (!showLandingView) {
          landingQuickBufferRef.current = "";
          setLandingQuickBuffer("");
          resetLandingQuickState();
          updateQuickJumpBuffer("");
          setShowLandingView(true);
        }
        return;
      }

      const bufferSnapshot = quickJumpBufferRef.current;
      const buffer = bufferSnapshot;
      const shouldCaptureQuickNav =
        (topControlsHiddenRef.current || buffer.length > 0) &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey;
      const isPlainEscape =
        !ctrlOrMeta && !altKey && !shiftKey && event.key === "Escape";

      if (isPlainEscape) {
        if (showLandingView) {
          event.preventDefault();
          setShowLandingView(false);
          return;
        }
        if (shouldCaptureQuickNav && buffer.length > 0) {
          event.preventDefault();
          updateQuickJumpBuffer("");
          return;
        }
        event.preventDefault();
        updateQuickJumpBuffer("");
        setShowLandingView(true);
        return;
      }

      if (showLandingView && !ctrlOrMeta && !altKey && !isFormField) {
        const landingBuffer = landingQuickBufferRef.current;
        if (event.key === "Tab") {
          event.preventDefault();
          if (landingBuffer.trim()) {
            processLandingQuickInput(landingBuffer, {
              cycle: true,
              keepBuffer: true,
              reverse: event.shiftKey,
            });
          }
          return;
        }
        if (event.key === "Enter") {
          event.preventDefault();
          const bufferValue = landingBuffer.trim();
          if (bufferValue) {
            const handled = parseAndExecuteSearch(bufferValue);
            if (handled) {
              landingQuickBufferRef.current = "";
              setLandingQuickBuffer("");
              resetLandingQuickState();
              setShowLandingView(false);
              return;
            }
          }
          if (landingHighlightedBookIndex !== null) {
            handleLandingBookSelect(landingHighlightedBookIndex);
          }
          return;
        }
        if (event.key === "Backspace") {
          event.preventDefault();
          if (landingBuffer.length > 0) {
            processLandingQuickInput(landingBuffer.slice(0, -1));
          }
          return;
        }
        if (event.key === " ") {
          event.preventDefault();
          processLandingQuickInput(`${landingBuffer} `);
          return;
        }
        if (/^[a-zA-Z0-9:\-]$/.test(event.key)) {
          event.preventDefault();
          processLandingQuickInput(`${landingBuffer}${event.key}`);
          return;
        }
      }

      if (
        event.key === "Control" &&
        event.ctrlKey &&
        !event.altKey &&
        !event.metaKey &&
        !event.shiftKey
      ) {
        event.preventDefault();
        toggleControlsVisibility();
        return;
      }

      if (!ctrlOrMeta && !altKey && !shiftKey && event.key === "?") {
        event.preventDefault();
        toggleHelpModal();
        return;
      }

      if (ctrlOrMeta && (altKey || shiftKey)) {
        const comboKey =
          event.key.length === 1 ? event.key.toLowerCase() : event.key;
        switch (comboKey) {
          case "f":
            focusSearchField();
            event.preventDefault();
            return;
          case "l":
            cycleLanguage();
            event.preventDefault();
            return;
          case "g":
            cycleVersion();
            event.preventDefault();
            return;
          case "b":
            focusBookSelectorControl();
            event.preventDefault();
            return;
          case "j":
            focusChapterSelectorControl();
            event.preventDefault();
            return;
          case "d":
            cycleDisplayMode();
            event.preventDefault();
            return;
          case "h":
            cycleHighlightMode();
            event.preventDefault();
            return;
          case "v":
            toggleVoiceSetting();
            event.preventDefault();
            return;
          case "c":
            toggleSecondaryCompare();
            event.preventDefault();
            return;
          case "m":
            if (isBibleIs) {
              cycleBibleIsMediaMode();
              event.preventDefault();
            }
            return;
          case "s":
            handleShare();
            event.preventDefault();
            return;
          case "]":
            increaseVersesPerPage();
            event.preventDefault();
            return;
          case "[":
            decreaseVersesPerPage();
            event.preventDefault();
            return;
          case ".":
            increaseFontSize();
            event.preventDefault();
            return;
          case ",":
            decreaseFontSize();
            event.preventDefault();
            return;
          case "t":
            toggleControlsVisibility();
            event.preventDefault();
            return;
          case "o":
            if (isBibleIs) {
              toggleLandingView();
              event.preventDefault();
            }
            return;
          default:
            break;
        }
      }

      if (isFormField && !ctrlOrMeta && !altKey) {
        // Do not process global shortcuts if user is typing in an input
        return;
      }

      // Prevent key-repeat flickering for toggle keys
      if (event.repeat && event.key === " ") {
        return;
      }

      if (shouldCaptureQuickNav) {
        if (event.key === "Tab") {
          const info = extractBookLookup(buffer);
          if (info.key) {
            event.preventDefault();
            selectBookByPrefixKey(info.key, true);
            return;
          }
        }
        if (event.key === "Enter") {
          event.preventDefault();
          if (buffer.trim()) {
            parseAndExecuteSearch(buffer);
          }
          updateQuickJumpBuffer("");
          return;
        }
        if (event.key === "Escape") {
          event.preventDefault();
          updateQuickJumpBuffer("");
          return;
        }
        if (event.key === "Backspace") {
          event.preventDefault();
          if (buffer.length > 0) {
            const nextVal = buffer.slice(0, -1);
            updateQuickJumpBuffer(nextVal);
            const info = extractBookLookup(nextVal);
            if (info.key) {
              selectBookByPrefixKey(info.key, false);
            }
          }
          return;
        }
        if (event.key === " ") {
          event.preventDefault();
          if (buffer.length > 0) {
            updateQuickJumpBuffer(`${buffer} `);
          }
          return;
        }
        if (/^[a-zA-Z0-9:\-]$/.test(event.key)) {
          const nextChar = event.key;
          const nextValue = buffer.length === 0 ? nextChar : `${buffer}${nextChar}`;
          updateQuickJumpBuffer(nextValue);
          if (/^[a-zA-Z]$/.test(nextChar)) {
            const info = extractBookLookup(nextValue);
            if (
              info.key &&
              nextValue.replace(/[^a-zA-Z]/g, "").length ===
                info.letters.replace(/[^a-zA-Z]/g, "").length
            ) {
              selectBookByPrefixKey(info.key, false);
            }
          }
          event.preventDefault();
          return;
        }
      }

      switch (event.key) {
        case " ":
          event.preventDefault();
          togglePlayPause();
          break;
        case "ArrowRight":
          handleNextRef.current();
          break;
        case "PageDown":
          handleNextRef.current();
          break;
        case "ArrowLeft":
          handlePrevRef.current();
          break;
        case "PageUp":
          handlePrevRef.current();
          break;
        case "ArrowUp":
          event.preventDefault();
          movePrevChapterRef.current();
          break;
        case "ArrowDown":
          event.preventDefault();
          moveNextChapterRef.current();
          break;
        case ">":
        case ".": // Shift + '.' is '>', but we catch it here anyway
          increaseFontSize();
          break;
        case "<":
        case ",": // Shift + ',' is '<'
          decreaseFontSize();
          break;
        case "+":
        case "=": // For shift+ or normal '+'
          event.preventDefault(); // Prevent browser zoom
          increaseVersesPerPage();
          break;
        case "-":
        case "_":
          event.preventDefault(); // Prevent browser zoom
          decreaseVersesPerPage();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    // Clean up on unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (!isDraggingNav) return undefined;
    const updatePosition = (clientX, clientY) => {
      const node = navRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const newLeft = Math.max(
        8,
        Math.min(window.innerWidth - rect.width - 8, clientX - navDragOffsetRef.current.x),
      );
      const newTop = Math.max(
        8,
        Math.min(window.innerHeight - rect.height - 8, clientY - navDragOffsetRef.current.y),
      );
      setNavPosition({ left: newLeft, top: newTop });
    };
    const handleMouseMove = (event) => {
      updatePosition(event.clientX, event.clientY);
    };
    const handleTouchMove = (event) => {
      const touch = event.touches?.[0];
      if (!touch) return;
      updatePosition(touch.clientX, touch.clientY);
      event.preventDefault();
    };
    const stopDrag = () => setIsDraggingNav(false);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", stopDrag);
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", stopDrag);
    window.addEventListener("touchcancel", stopDrag);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stopDrag);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", stopDrag);
      window.removeEventListener("touchcancel", stopDrag);
    };
  }, [isDraggingNav]);

  // Load available speech synthesis voices
  useEffect(() => {
    function loadVoices() {
      const voices = window.speechSynthesis.getVoices();
      const english = voices.filter((v) =>
        v.lang.toLowerCase().startsWith("en"),
      );
      setAvailableVoices(english);
      const google = english.find((v) => v.name.includes("Google"));
      if (google) setSelectedVoice(google.name);
    }
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
  }, []);

  /**
   * Whenever currentBook/currentChapter/currentVersion/currentLanguage changes,
   * fetch the verses from the API.
   */
  useEffect(() => {
    fetchVersesForSelection({
      book: currentBook,
      chapter: currentChapter,
      version: currentVersion,
      language: currentLanguage,
      verses: currentVerseRange,
      setVerses: setCurrentVerses,
      setError: setFetchError,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentBook,
    currentChapter,
    currentVersion,
    currentLanguage,
    currentVerseRange,
    source,
  ]);

  useEffect(() => {
    if (!secondaryEnabled) {
      setSecondaryVerses([]);
      setSecondaryError("");
      return;
    }
    fetchVersesForSelection({
      book: currentBook,
      chapter: currentChapter,
      version: secondaryVersion,
      language: secondaryLanguage,
      verses: currentVerseRange,
      setVerses: setSecondaryVerses,
      setError: setSecondaryError,
    });
  }, [
    secondaryEnabled,
    secondaryLanguage,
    secondaryVersion,
    currentBook,
    currentChapter,
    currentVerseRange,
    source,
  ]);

  useEffect(() => {
    if (!isBibleIs) return;
    const isAudioMode =
      bibleIsMediaType === "audio" || bibleIsMediaType === "audioDrama";
    if (!isAudioMode) {
      setAudioData(null);
      setAudioError("");
      setAudioIsLoading(false);
      setAudioFilesetId("");
      setAudioTimestamps([]);
      setCurrentAudioVerse(null);
      resetAudioElement();
      return;
    }

    const bookId = bibleBrainBookIds[currentBook];
    if (!bookId) {
      setAudioError("Unsupported book selection for Bible.is audio.");
      setAudioData(null);
      setAudioIsLoading(false);
      setAudioFilesetId("");
      setAudioTimestamps([]);
      setCurrentAudioVerse(null);
      resetAudioElement();
      return;
    }

    const testamentKey = getTestamentKey(currentBook);
    const mediaKey = bibleIsMediaType === "audio" ? "audio" : "audioDrama";
    const filesetId = resolveBibleIsFileset(
      currentLanguage,
      currentVersion,
      testamentKey,
      mediaKey,
    );

    if (!filesetId) {
      setAudioError(
        bibleIsMediaType === "audio"
          ? "Audio is not available for this selection."
          : "Audio drama is not available for this selection.",
      );
      setAudioData(null);
      setAudioIsLoading(false);
      setAudioFilesetId("");
      setAudioTimestamps([]);
      setCurrentAudioVerse(null);
      resetAudioElement();
      return;
    }

    resetAudioElement();

    const params = new URLSearchParams({
      v: BIBLE_IS_API_VERSION,
      key: BIBLEBRAIN_API_KEY,
    });
    applyVerseParams(params, currentVerseRange);

    const apiUrl = `${BIBLE_IS_API_BASE}/bibles/filesets/${filesetId}/${bookId}/${currentChapter}?${params.toString()}`;
    setAudioIsLoading(true);
    fetchJsonWithCors(apiUrl)
      .then((data) => {
        if (!data || !Array.isArray(data.data) || data.data.length === 0) {
          setAudioError("No audio returned for this chapter.");
          setAudioData(null);
          setAudioFilesetId("");
          setAudioTimestamps([]);
          setCurrentAudioVerse(null);
          resetAudioElement();
          return;
        }
        const entry = data.data[0];
        const normalizedEntry = {
          ...entry,
          path: ensureBibleIsAuthParams(entry.path),
        };
        setAudioData(normalizedEntry);
        setAudioError("");
        setAudioFilesetId(filesetId);
        const audioEl = audioRef.current;
        if (audioEl) {
          audioEl.src = normalizedEntry.path;
          audioEl.load();
        }
      })
      .catch((err) => {
        console.error("Error fetching Bible.is audio:", err);
        setAudioError("Unable to load audio. Please try again.");
        setAudioData(null);
        setAudioFilesetId("");
        setAudioTimestamps([]);
        setCurrentAudioVerse(null);
        resetAudioElement();
      })
      .finally(() => {
        setAudioIsLoading(false);
      });
  }, [
    isBibleIs,
    bibleIsMediaType,
    currentBook,
    currentChapter,
    currentVersion,
    currentLanguage,
    currentVerseRange,
  ]);

  useEffect(() => {
    if (!isBibleIs || bibleIsMediaType === "text" || !audioFilesetId) {
      setAudioTimestamps([]);
      setCurrentAudioVerse(null);
      return;
    }

    const bookId = bibleBrainBookIds[currentBook];
    if (!bookId) {
      setAudioTimestamps([]);
      setCurrentAudioVerse(null);
      return;
    }

    const params = new URLSearchParams({
      v: BIBLE_IS_API_VERSION,
      key: BIBLEBRAIN_API_KEY,
    });
    applyVerseParams(params, currentVerseRange);

    const apiUrl = `${BIBLE_IS_API_BASE}/timestamps/${audioFilesetId}/${bookId}/${currentChapter}?${params.toString()}`;
    fetchJsonWithCors(apiUrl)
      .then((data) => {
        if (!data || !Array.isArray(data.data)) {
          setAudioTimestamps([]);
          setCurrentAudioVerse(null);
          return;
        }
        const mapped = data.data
          .map((entry) => ({
            verse: parseInt(entry.verse_start, 10),
            timestamp: Number(entry.timestamp) || 0,
          }))
          .filter((entry) => !Number.isNaN(entry.verse) && entry.verse > 0)
          .sort((a, b) => a.timestamp - b.timestamp);
        setAudioTimestamps(mapped);
      })
      .catch((err) => {
        console.error("Error fetching Bible.is audio timestamps:", err);
        setAudioTimestamps([]);
        setCurrentAudioVerse(null);
      });
  }, [
    isBibleIs,
    bibleIsMediaType,
    audioFilesetId,
    currentBook,
    currentChapter,
    currentVerseRange,
  ]);

  useEffect(() => {
    if (!isBibleIs) return;
    if (bibleIsMediaType !== "audio" && bibleIsMediaType !== "audioDrama") return;
    if (!audioData || audioIsLoading || audioError) return;
    const audioEl = audioRef.current;
    if (!audioEl) return;
    if (!audioPendingAutoPlayRef.current) return;
    audioPendingAutoPlayRef.current = false;
    audioEl.currentTime = 0;
    audioAutoAdvanceRef.current = true;
    audioEl
      .play()
      .catch((err) => {
        console.error("Bible.is audio autoplay failed:", err);
        audioAutoAdvanceRef.current = false;
      });
  }, [
    isBibleIs,
    bibleIsMediaType,
    audioData,
    audioIsLoading,
    audioError,
  ]);

  useEffect(() => {
    if (!isBibleIs) return;
    if (bibleIsMediaType !== "text" && isPlaying) {
      pauseAutoAdvance();
    }
  }, [bibleIsMediaType, isBibleIs, isPlaying]);

  useEffect(() => {
    if (!isBibleIs) return;
    if (bibleIsMediaType !== "text" && voiceEnabled) {
      setVoiceEnabled(false);
    }
  }, [bibleIsMediaType, isBibleIs, voiceEnabled]);

  useEffect(() => {
    if (!isBibleIs || bibleIsMediaType === "text") {
      setCurrentAudioVerse(null);
    }
  }, [isBibleIs, bibleIsMediaType]);

  useEffect(() => {
    setCurrentAudioVerse(null);
  }, [currentVerses]);

  useEffect(() => {
    if (!isBibleIs) return;
    if (bibleIsMediaType === "audio") {
      const available = resolveBibleIsFileset(
        currentLanguage,
        currentVersion,
        getTestamentKey(currentBook),
        "audio",
      );
      if (!available) {
        setBibleIsMediaType("text");
      }
    } else if (bibleIsMediaType === "audioDrama") {
      const available = resolveBibleIsFileset(
        currentLanguage,
        currentVersion,
        getTestamentKey(currentBook),
        "audioDrama",
      );
      if (!available) {
        setBibleIsMediaType("text");
      }
    } else if (bibleIsMediaType === "video") {
      const available = resolveBibleIsFileset(
        currentLanguage,
        currentVersion,
        getTestamentKey(currentBook),
        "video",
      );
      if (!available) {
        setBibleIsMediaType("text");
      }
    }
  }, [
    isBibleIs,
    bibleIsMediaType,
    currentLanguage,
    currentVersion,
    currentBook,
  ]);

  useEffect(() => {
    if (!isBibleIs) return;
    if (bibleIsMediaType !== "video") {
      setVideoData(null);
      setVideoError("");
      setVideoIsLoading(false);
      setVideoVerseTimings([]);
      resetVideoElement();
      return;
    }

    const bookId = bibleBrainBookIds[currentBook];
    if (!bookId) {
      setVideoError("Unsupported book selection for Bible.is video.");
      setVideoData(null);
      setVideoIsLoading(false);
      resetVideoElement();
      return;
    }

    const testamentKey = getTestamentKey(currentBook);
    const filesetId = resolveBibleIsFileset(
      currentLanguage,
      currentVersion,
      testamentKey,
      "video",
    );

    if (!filesetId) {
      setVideoError("Video is not available for this selection.");
      setVideoData(null);
      setVideoIsLoading(false);
      resetVideoElement();
      return;
    }

    resetVideoElement();

    const params = new URLSearchParams({
      v: BIBLE_IS_API_VERSION,
      key: BIBLEBRAIN_API_KEY,
      book_id: bookId,
      chapter: currentChapter,
    });

    const apiUrl = `${BIBLE_IS_API_BASE}/bibles/filesets/${filesetId}?${params.toString()}`;
    setVideoIsLoading(true);
    fetchJsonWithCors(apiUrl)
      .then((data) => {
        if (!data || !Array.isArray(data.data) || data.data.length === 0) {
          setVideoError("No video returned for this chapter.");
          setVideoData(null);
          resetVideoElement();
          return;
        }
        const entry = data.data[0];
        const normalizedEntry = {
          ...entry,
          path: ensureBibleIsAuthParams(entry.path),
        };
        setVideoData(normalizedEntry);
        setVideoError("");
      })
      .catch((err) => {
        console.error("Error fetching Bible.is video:", err);
        setVideoError("Unable to load video. Please try again.");
        setVideoData(null);
        resetVideoElement();
      })
      .finally(() => {
        setVideoIsLoading(false);
      });
  }, [
    isBibleIs,
    bibleIsMediaType,
    currentBook,
    currentChapter,
    currentLanguage,
    currentVersion,
  ]);

  useEffect(() => {
    if (!isBibleIs || bibleIsMediaType !== "video" || !videoData) {
      setVideoVerseTimings([]);
      return;
    }
    const startVerse = parseInt(videoData.verse_start, 10);
    const endVerse = parseInt(videoData.verse_end, 10);
    const versesInRange = currentVerses.filter((verse) => {
      if (Number.isFinite(startVerse) && verse.verse < startVerse) return false;
      if (Number.isFinite(endVerse) && verse.verse > endVerse) return false;
      return true;
    });
    if (!versesInRange.length) {
      setVideoVerseTimings([]);
      return;
    }
    const verseWeights = versesInRange.map((verse) => {
      const words = verse.text ? verse.text.trim().split(/\s+/).length : 0;
      return words > 0 ? words : 1;
    });
    const totalWeight = verseWeights.reduce((sum, weight) => sum + weight, 0);
    const mediaDuration =
      Number(videoData.duration) ||
      videoRef.current?.duration ||
      0;
    if (!totalWeight || !mediaDuration) {
      setVideoVerseTimings([]);
      return;
    }
    let cumulative = 0;
    const timings = versesInRange.map((verse, index) => {
      const startTime = (cumulative / totalWeight) * mediaDuration;
      cumulative += verseWeights[index];
      return { verse: verse.verse, startTime };
    });
    setVideoVerseTimings(timings);
  }, [
    bibleIsMediaType,
    currentVerses,
    isBibleIs,
    videoData,
    videoMetadataTick,
  ]);

  useEffect(() => {
    if (!isBibleIs) return;
    if (bibleIsMediaType !== "video") return;
    if (!videoData || videoIsLoading || videoError) return;
    const videoEl = videoRef.current;
    if (!videoEl) return;
    if (!videoPendingAutoPlayRef.current) return;
    videoPendingAutoPlayRef.current = false;
    videoAutoAdvanceRef.current = true;
    videoEl
      .play()
      .catch((err) => {
        console.error("Bible.is video autoplay failed:", err);
        videoAutoAdvanceRef.current = false;
      });
  }, [
    isBibleIs,
    bibleIsMediaType,
    videoData,
    videoIsLoading,
    videoError,
  ]);

  useEffect(() => {
    const videoEl = videoRef.current;
    const cleanupHls = () => {
      if (hlsInstanceRef.current) {
        hlsInstanceRef.current.destroy();
        hlsInstanceRef.current = null;
      }
    };
    if (!videoEl) {
      cleanupHls();
      return undefined;
    }

    if (!isBibleIs || bibleIsMediaType !== "video" || !videoData?.path) {
      cleanupHls();
      videoEl.removeAttribute("src");
      videoEl.removeAttribute("poster");
      videoEl.load();
      return undefined;
    }

    if (videoData.thumbnail) {
      videoEl.poster = videoData.thumbnail;
    } else {
      videoEl.removeAttribute("poster");
    }

    const sourceUrl = videoData.path;
    const nativeSupport = videoEl.canPlayType("application/vnd.apple.mpegurl");
    if (nativeSupport === "probably" || nativeSupport === "maybe") {
      cleanupHls();
      videoEl.src = sourceUrl;
      videoEl.load();
      return cleanupHls;
    }

    if (Hls.isSupported()) {
      cleanupHls();
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
      });
      hlsInstanceRef.current = hls;
      hls.attachMedia(videoEl);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(sourceUrl);
      });
      hls.on(Hls.Events.ERROR, (_, data) => {
        console.error("Bible.is video HLS error:", data);
        if (!data?.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          hls.startLoad();
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
        } else {
          setVideoError("Video playback error. Please try again.");
          cleanupHls();
        }
      });
      return () => {
        hls.destroy();
        hlsInstanceRef.current = null;
      };
    }

    const unsupportedMsg =
      "Your browser does not support this streaming video format. Please try a different browser.";
    setVideoError((prev) => (prev === unsupportedMsg ? prev : unsupportedMsg));
    cleanupHls();
    return undefined;
  }, [isBibleIs, bibleIsMediaType, videoData]);

  /**
   * Whenever we update the verses, re-display from the current page.
   */
  useEffect(() => {
    // reset verse page if new verses or mode changes
    setCurrentVersePage(0);
  }, [currentVerses, displayMode]);

  /**
   * Each time something changes that affects the shareable state,
   * we can update the URL (like your original code).
   */
  useEffect(() => {
    updateURL();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentBookIndex,
    currentChapter,
    currentVersion,
    currentLanguage,
    versesPerPage,
    wordsPerMinute,
    currentVersePage,
    currentVerseRange,
    customSequenceEntries,
    customSequenceInput,
    autoPlayNext,
  ]);

  // Prepare tokens for the current slide whenever verses or page change
  useEffect(() => {
    const versesToShow = getDisplayedVerses();
    const combined = versesToShow.map((v) => `${v.verse}. ${v.text}`).join(" ");
    const tokens =
      combined.match(/\b\w+(?:['’]\w+)*\b|\s+|[^\s\w]+/g) || [];
    setSlideTokens(tokens);
    setCurrentTokenIndex(0);
  }, [currentVersePage, currentVerses, highlightMode]);

  // Play or highlight slide whenever tokens update while playing
  useEffect(() => {
    if (!isPlaying || slideTokens.length === 0) return;
    if (voiceEnabled) {
      speakCurrentSlide();
    } else if (highlightMode === "word") {
      highlightNextToken(0);
    }
  }, [slideTokens, highlightMode, voiceEnabled, isPlaying]);

  // -----------------------------
  //     Main Fetch Function
  // -----------------------------
  async function fetchVersesForSelection({
    book,
    chapter,
    version,
    language,
    verses,
    setVerses,
    setError,
  }) {
    try {
      if (setError) setError("");
      if (isBibleIs) {
        const testamentKey = getTestamentKey(book);
        const bookId = bibleBrainBookIds[book];

        if (!bookId) {
          if (setError) setError("Unsupported book selection for Bible.is.");
          setVerses([]);
          return;
        }

        const filesetId = resolveBibleIsFileset(
          language,
          version,
          testamentKey,
          "text",
        );

        if (!filesetId) {
          if (setError)
            setError(
              "Bible.is does not provide this translation for the selected language or version.",
            );
          setVerses([]);
          return;
        }

        const params = new URLSearchParams({
          v: BIBLE_IS_API_VERSION,
          key: BIBLEBRAIN_API_KEY,
        });

        applyVerseParams(params, verses);

        const apiUrl = `${BIBLE_IS_API_BASE}/bibles/filesets/${filesetId}/${bookId}/${chapter}?${params.toString()}`;
        const data = await fetchJsonWithCors(apiUrl);

        if (!data || !Array.isArray(data.data) || data.data.length === 0) {
          if (setError)
            setError("No verses available from Bible.is for this selection.");
          setVerses([]);
          return;
        }

        const mapped = data.data.map((v) => ({
          verse: parseInt(v.verse_start, 10),
          text: v.verse_text || v.text || "",
        }));
        setVerses(mapped);
        return;
      }

      if (version === "NKJV") {
        const bookId = bibleBrainBookIds[book];
        if (!bookId) {
          setVerses([]);
          return;
        }
        let apiUrl = `https://biblebrain.com/api/bible/verses?bible_id=${NKJV_BIBLE_ID}&book_id=${bookId}&chapter=${chapter}`;
        if (verses) {
          const [start, end] = verses.split("-");
          apiUrl += `&verse_start=${start}`;
          apiUrl += `&verse_end=${end || start}`;
        }
        apiUrl += `&key=${BIBLEBRAIN_API_KEY}`;
        const data = await fetchJsonWithCors(apiUrl);
        if (!data || !data.data) {
          setVerses([]);
          return;
        }
        const mapped = data.data.map((v) => ({
          verse: parseInt(v.verse_start, 10),
          text: v.verse_text || v.text,
        }));
        setVerses(mapped);
        return;
      }

      // Default bible-api.com implementation
      let passage = `${book} ${chapter}`;
      if (verses) {
        passage += `:${verses}`;
      }
      const apiUrl = `https://bible-api.com/${encodeURIComponent(passage)}?translation=${version}`;
      const data = await fetchJsonWithCors(apiUrl);

      if (data.error) {
        setVerses([]);
        if (setError) setError("Unable to load verses for this selection.");
        return;
      }
      setVerses(data.verses);
    } catch (err) {
      console.error("Error fetching verses:", err);
      if (setError) setError("Unable to load verses. Please try again.");
      setVerses([]);
    }
  }

  // -----------------------------
  //       Rendering Logic
  // -----------------------------
  function getDisplayedVerses() {
    if (displayMode === "full") {
      return currentVerses;
    }
    const start = currentVersePage * versesPerPage;
    const end = start + versesPerPage;
    return currentVerses.slice(start, end);
  }

  function getFirstVisibleVerseNumber() {
    const versesToShow = getDisplayedVerses();
    if (versesToShow && versesToShow.length > 0) {
      return versesToShow[0]?.verse ?? null;
    }
    return currentVerses?.[0]?.verse ?? null;
  }

  function renderSlideContent() {
    if (fetchError) {
      return <div className="br-no-verses">{fetchError}</div>;
    }
    const versesToShow = getDisplayedVerses();
    if (versesToShow.length === 0) {
      return <div className="br-no-verses">No verses available.</div>;
    }

    const comparisonActive =
      secondaryEnabled && !secondaryError && secondaryVerses.length > 0;
    const firstDisplayedVerse = versesToShow[0]?.verse;

    return (
      <div
        className="br-slide-content"
        style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
      >
        {versesToShow.map((verse) => {
          const secondaryText = secondaryVerseMap.get(verse.verse) || "";
          const isAudioActive =
            isBibleIs && isAudioMode && currentAudioVerse === verse.verse;
          const shouldHighlightPrimary =
            highlightMode !== "none" &&
            (isAudioActive ||
              (!isBibleIs &&
                highlightMode === "verse" &&
                isPlaying &&
                verse.verse === firstDisplayedVerse));
          const primaryStyle = shouldHighlightPrimary
            ? {
                backgroundColor: "rgba(255, 255, 255, 0.08)",
                borderRadius: "8px",
                padding: "0.35rem 0.65rem",
              }
            : undefined;

          return (
            <div
              key={`slide-row-${verse.verse}`}
              className="br-verse-row"
              style={{ display: "flex", gap: "1.5rem", alignItems: "flex-start" }}
            >
              <div
                className="br-verse"
                style={{
                  fontSize: `${fontSize}px`,
                  flex: 1,
                  ...(primaryStyle || {}),
                }}
              >
                <span className="br-verse-number">{verse.verse}.</span>{" "}
                <span>{verse.text}</span>
              </div>
              {comparisonActive && (
                <div
                  className="br-verse br-verse-secondary"
                  style={{
                    fontSize: `${fontSize * 0.95}px`,
                    flex: 1,
                    ...(shouldHighlightPrimary && isAudioActive
                      ? primaryStyle || {}
                      : {}),
                  }}
                >
                  <span className="br-verse-number">{verse.verse}.</span>{" "}
                  <span>{secondaryText || "\u2014"}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  function renderWordHighlightView() {
    if (fetchError) {
      return <div className="br-no-verses">{fetchError}</div>;
    }
    if (slideTokens.length === 0) {
      return <div className="br-no-verses">No verses available.</div>;
    }
    const comparisonActive =
      secondaryEnabled && !secondaryError && secondaryVerses.length > 0;
    const versesToShow = getDisplayedVerses();

    return (
      <div
        className="br-word-highlight-layout"
        style={{ display: "flex", gap: "1.5rem", alignItems: "flex-start" }}
      >
        <div className="br-verse" style={{ fontSize: `${fontSize}px`, flex: 1 }}>
          {slideTokens.map((t, idx) => (
            <span
              key={idx}
              className={idx === currentTokenIndex ? "br-word-highlight" : ""}
              style={
                idx === currentTokenIndex
                  ? {
                      backgroundColor: "rgba(255, 255, 255, 0.12)",
                      color: "#f0f0f0",
                      borderRadius: "6px",
                      padding: "0 0.25rem",
                    }
                  : undefined
              }
            >
              {t}
            </span>
          ))}
        </div>
        {comparisonActive && (
          <div
            className="br-verse br-verse-secondary"
            style={{ fontSize: `${fontSize * 0.95}px`, flex: 1 }}
          >
            {versesToShow.map((verse) => {
              const secondaryText = secondaryVerseMap.get(verse.verse) || "";
              const secondaryHighlight =
                highlightMode !== "none" &&
                ((isBibleIs && isAudioMode && currentAudioVerse === verse.verse) ||
                  (highlightMode === "verse" &&
                    isPlaying &&
                    (!isBibleIs || bibleIsMediaType === "text")));
              return (
                <div
                  key={`word-secondary-${verse.verse}`}
                  style={
                    secondaryHighlight
                      ? {
                          backgroundColor: "rgba(255, 255, 255, 0.12)",
                          color: "#f0f0f0",
                          borderRadius: "6px",
                          padding: "0 0.2rem",
                        }
                      : undefined
                  }
                >
                  <span className="br-verse-number">{verse.verse}.</span>{" "}
                  <span>{secondaryText || "\u2014"}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  function renderDefaultContent() {
    if (fetchError) {
      return <div className="br-no-verses">{fetchError}</div>;
    }
    const versesToShow = getDisplayedVerses();
    if (versesToShow.length === 0) {
      return <div className="br-no-verses">No verses available.</div>;
    }

    const comparisonActive =
      secondaryEnabled && !secondaryError && secondaryVerses.length > 0;

    const firstVerse = versesToShow[0]?.verse;
    const primaryParagraph = versesToShow
      .map((verse) => `${verse.verse}. ${verse.text}`)
      .join(" ");
    const secondaryParagraph = versesToShow
      .map((verse) => {
        const secondaryText = secondaryVerseMap.get(verse.verse) || "";
        return secondaryText ? `${verse.verse}. ${secondaryText}` : "";
      })
      .filter(Boolean)
      .join(" ");

    const shouldHighlightPrimary =
      highlightMode !== "none" &&
      (isBibleIs
        ? isAudioMode && currentAudioVerse === firstVerse
        : highlightMode === "verse" && isPlaying);
    const shouldHighlightSecondary =
      comparisonActive &&
      highlightMode !== "none" &&
      (isBibleIs
        ? isAudioMode && currentAudioVerse === firstVerse
        : highlightMode === "verse" && isPlaying);

    const paragraphHighlight = shouldHighlightPrimary
      ? {
          backgroundColor: "rgba(255, 255, 255, 0.08)",
          borderRadius: "8px",
          padding: "0.85rem",
        }
      : undefined;
    const secondaryHighlight = shouldHighlightSecondary
      ? {
          backgroundColor: "rgba(255, 255, 255, 0.08)",
          borderRadius: "8px",
          padding: "0.85rem",
        }
      : undefined;

    return (
      <div
        className="br-default-mode"
        style={{ display: "flex", gap: "1.5rem", alignItems: "flex-start" }}
      >
        <p
          style={{
            fontSize: `${fontSize}px`,
            flex: 1,
            lineHeight: 1.5,
            ...paragraphHighlight,
          }}
        >
          {primaryParagraph}
        </p>
        {comparisonActive && (
          <p
            style={{
              fontSize: `${fontSize * 0.95}px`,
              flex: 1,
              lineHeight: 1.5,
              ...secondaryHighlight,
            }}
          >
            {secondaryParagraph || "\u2014"}
          </p>
        )}
      </div>
    );
  }

  function renderFullContent() {
    if (fetchError) {
      return <div className="br-no-verses">{fetchError}</div>;
    }
    if (currentVerses.length === 0) {
      return <div className="br-no-verses">No verses available.</div>;
    }

    const comparisonActive =
      secondaryEnabled && !secondaryError && secondaryVerses.length > 0;

    const highlightStyle = {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
      borderRadius: "8px",
      padding: "0.65rem 0.85rem",
    };
    const firstFullVerse = currentVerses[0]?.verse;

  if (comparisonActive) {
    return (
      <div
        className="br-full-mode"
        style={{ display: "flex", gap: "2rem", alignItems: "flex-start" }}
      >
          <div style={{ flex: 1, fontSize: `${fontSize}px` }}>
            {currentVerses.map((verse) => {
              const isAudioActive =
                isBibleIs && isAudioMode && currentAudioVerse === verse.verse;
              const shouldHighlight =
                highlightMode !== "none" &&
                (isAudioActive ||
                  (!isBibleIs &&
                    highlightMode === "verse" &&
                    isPlaying &&
                    verse.verse === firstFullVerse));
              return (
                <div
                  key={`full-primary-${verse.verse}`}
                  style={shouldHighlight ? highlightStyle : undefined}
                >
                  <span className="br-verse-number">{verse.verse}.</span>{" "}
                  <span>{verse.text}</span>
                </div>
              );
            })}
          </div>
          <div style={{ flex: 1, fontSize: `${fontSize * 0.95}px` }}>
            {currentVerses.map((verse) => {
              const secondaryText = secondaryVerseMap.get(verse.verse) || "";
              const secondaryHighlight =
                highlightMode !== "none" &&
                (currentAudioVerse === verse.verse ||
                  (!isBibleIs &&
                    highlightMode === "verse" &&
                    isPlaying &&
                    verse.verse === firstFullVerse));
              return (
                <div
                  key={`full-secondary-${verse.verse}`}
                  style={secondaryHighlight ? highlightStyle : undefined}
                >
                  <span className="br-verse-number">{verse.verse}.</span>{" "}
                  <span>{secondaryText || "\u2014"}</span>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div
        className="br-full-mode-single"
        style={{
          columnCount: 2,
          columnGap: "2rem",
          fontSize: `${fontSize}px`,
        }}
      >
        {currentVerses.map((verse) => {
          const isAudioActive =
            isBibleIs && isAudioMode && currentAudioVerse === verse.verse;
          const shouldHighlight =
            highlightMode !== "none" &&
            (isAudioActive ||
              (!isBibleIs &&
                highlightMode === "verse" &&
                isPlaying &&
                verse.verse === firstFullVerse));
          return (
            <div
              key={`full-single-${verse.verse}`}
              style={{
                breakInside: "avoid",
                ...(shouldHighlight ? highlightStyle : {}),
              }}
            >
              <span className="br-verse-number">{verse.verse}.</span>{" "}
              <span>{verse.text}</span>
            </div>
          );
        })}
      </div>
    );
  }

  function renderContentArea() {
    if (fetchError) return <div className="br-no-verses">{fetchError}</div>;
    if (currentVerses.length === 0) return "Loading...";
    if (displayMode === "default") return renderDefaultContent();
    if (displayMode === "full") return renderFullContent();
    if (highlightMode === "word" && isPlaying) return renderWordHighlightView();
    return renderSlideContent();
  }

  // -----------------------------
  //       Navigation
  // -----------------------------
  function handleNext() {
    // If we don't have any verses loaded just ignore the event
    if (currentVerses.length === 0) return false;
    const hasLockedRange = Boolean(currentVerseRange?.trim());

    // If there's another “page” of verses in the current chapter, go to it
    if ((currentVersePage + 1) * versesPerPage < currentVerses.length) {
      if (isPlayingRef.current && utteranceRef.current) {
        window.speechSynthesis.cancel();
        utteranceRef.current = null;
      }
      setCurrentVersePage((prev) => prev + 1);
      return true;
    } else if (isCustomSequenceActive) {
      if (customSequenceIndex < customSequenceEntries.length - 1) {
        if (isPlayingRef.current && utteranceRef.current) {
          window.speechSynthesis.cancel();
          utteranceRef.current = null;
        }
        setCustomSequenceIndex((prev) => prev + 1);
        return true;
      }
      return false;
    } else if (!hasLockedRange) {
      // Otherwise, go to next chapter
      return Boolean(moveToNextChapter());
    }
    return false;
  }

  function handlePrev() {
    if (currentVerses.length === 0) return false;
    const hasLockedRange = Boolean(currentVerseRange?.trim());

    if (currentVersePage > 0) {
      if (isPlayingRef.current && utteranceRef.current) {
        window.speechSynthesis.cancel();
        utteranceRef.current = null;
      }
      setCurrentVersePage((prev) => prev - 1);
      return true;
    } else if (isCustomSequenceActive) {
      if (customSequenceIndex > 0) {
        if (isPlayingRef.current && utteranceRef.current) {
          window.speechSynthesis.cancel();
          utteranceRef.current = null;
        }
        setCustomSequenceIndex((prev) => prev - 1);
        return true;
      }
      return false;
    } else if (!hasLockedRange) {
      // Move to previous chapter
      return Boolean(moveToPrevChapter());
    }
    return false;
  }

  // Keep navigation refs in sync so key handlers use latest logic
  useEffect(() => {
    handleNextRef.current = handleNext;
    handlePrevRef.current = handlePrev;
    moveNextChapterRef.current = moveToNextChapter;
    movePrevChapterRef.current = moveToPrevChapter;
  });

  useTouchNavigation(verseTouchTargetRef, {
    onLeft: handlePrev,
    onRight: handleNext,
  });

  useEffect(() => {
    if (!isEmbeddedRef.current || typeof window === "undefined") {
      return undefined;
    }
    const handleSessionNavigation = (event) => {
      if (!event || typeof event.data !== "object" || event.data.type !== SESSION_NAVIGATION_MESSAGE) {
        return;
      }
      const expectedOrigin = parentOriginRef.current;
      if (expectedOrigin !== "*" && event.origin && event.origin !== expectedOrigin) {
        return;
      }
      const { action, requestId } = event.data;
      if (!requestId) return;
      if (action === "next") {
        const handled = handleNext();
        sendSessionNavigationResponse(requestId, action, handled);
      } else if (action === "previous") {
        const handled = handlePrev();
        sendSessionNavigationResponse(requestId, action, handled);
      }
    };
    window.addEventListener("message", handleSessionNavigation);
    return () => window.removeEventListener("message", handleSessionNavigation);
  }, [handleNext, handlePrev, sendSessionNavigationResponse]);

  useEffect(() => {
    bookSelectionState.current.bookIndex = currentBookIndex;
  }, [currentBookIndex]);

  useEffect(() => {
    const currentMeta = books[currentBookIndex];
    if (!currentMeta) return;
    const totalChapters = currentMeta.chapters ?? 1;
    if (currentChapter < 1) {
      setCurrentChapter(1);
      setCurrentVersePage(0);
    } else if (currentChapter > totalChapters) {
      setCurrentChapter(totalChapters);
      setCurrentVersePage(0);
    }
  }, [books, currentBookIndex, currentChapter]);
  function findNextChapter(bookIdx, chapter) {
    let probeBookIndex = bookIdx;
    let probeChapter = chapter + 1;
    while (probeBookIndex < books.length) {
      const total = books[probeBookIndex]?.chapters ?? 0;
      if (total > 0 && probeChapter >= 1 && probeChapter <= total) {
        return { bookIndex: probeBookIndex, chapter: probeChapter };
      }
      probeBookIndex += 1;
      if (probeBookIndex >= books.length) break;
      probeChapter = 1;
    }
    return null;
  }

  function findPreviousChapter(bookIdx, chapter) {
    let probeBookIndex = bookIdx;
    let probeChapter = chapter - 1;
    while (probeBookIndex >= 0) {
      const total = books[probeBookIndex]?.chapters ?? 0;
      if (total > 0 && probeChapter >= 1 && probeChapter <= total) {
        return { bookIndex: probeBookIndex, chapter: probeChapter };
      }
      probeBookIndex -= 1;
      if (probeBookIndex < 0) break;
      probeChapter = books[probeBookIndex]?.chapters ?? 1;
    }
    return null;
  }

  function moveToNextChapter(autoAdvance = false) {
    if (isCustomSequenceActive) {
      if (customSequenceIndex < customSequenceEntries.length - 1) {
        if (isPlayingRef.current && utteranceRef.current) {
          window.speechSynthesis.cancel();
          utteranceRef.current = null;
        }
        setCustomSequenceIndex((prev) => prev + 1);
        return true;
      }
      if (autoAdvance) {
        audioAutoAdvanceRef.current = false;
        videoAutoAdvanceRef.current = false;
        audioPendingAutoPlayRef.current = false;
        videoPendingAutoPlayRef.current = false;
        isPlayingRef.current = false;
        setIsPlaying(false);
      }
      return false;
    }
    if (currentVerseRange?.trim()) {
      if (autoAdvance) {
        audioAutoAdvanceRef.current = false;
        videoAutoAdvanceRef.current = false;
        audioPendingAutoPlayRef.current = false;
        videoPendingAutoPlayRef.current = false;
        isPlayingRef.current = false;
        setIsPlaying(false);
      }
      return false;
    }
    const target = findNextChapter(currentBookIndex, currentChapter);
    if (!target) {
      if (!autoAdvance) {
        alert("Reached the end of the Bible.");
      } else {
        audioAutoAdvanceRef.current = false;
        videoAutoAdvanceRef.current = false;
        audioPendingAutoPlayRef.current = false;
        videoPendingAutoPlayRef.current = false;
        isPlayingRef.current = false;
        setIsPlaying(false);
      }
      return false;
    }

    const { bookIndex: nextBookIndex, chapter: nextChapter } = target;

    if (isPlayingRef.current && utteranceRef.current) {
      window.speechSynthesis.cancel();
      utteranceRef.current = null;
    }

    if (nextBookIndex !== currentBookIndex) {
      const nextBookMeta = books[nextBookIndex];
      if (nextBookMeta) {
        setCurrentBookIndex(nextBookIndex);
        setCurrentBook(nextBookMeta.name);
        bookSelectionState.current.bookIndex = nextBookIndex;
        bookSelectionState.current.prefix = "";
        bookSelectionState.current.cursor = 0;
      }
    }
    setCurrentChapter(nextChapter);
    setCurrentVersePage(0);

    if (autoAdvance) {
      if (displayMode !== "full") {
        setCurrentTokenIndex(0);
      }
      if (
        isBibleIs &&
        (bibleIsMediaType === "audio" || bibleIsMediaType === "audioDrama")
      ) {
        audioPendingAutoPlayRef.current = audioAutoAdvanceRef.current;
      } else if (isBibleIs && bibleIsMediaType === "video") {
        videoPendingAutoPlayRef.current = videoAutoAdvanceRef.current;
      }
    }
    return true;
  }

  function moveToPrevChapter() {
    if (isCustomSequenceActive) {
      if (customSequenceIndex > 0) {
        if (isPlayingRef.current && utteranceRef.current) {
          window.speechSynthesis.cancel();
          utteranceRef.current = null;
        }
        setCustomSequenceIndex((prev) => prev - 1);
        return true;
      }
      alert("You are at the beginning of the custom sequence.");
      return false;
    }
    if (currentVerseRange?.trim()) {
      return false;
    }
    const target = findPreviousChapter(currentBookIndex, currentChapter);
    if (!target) {
      alert("You are at the beginning of the Bible.");
      return false;
    }

    const { bookIndex: nextBookIndex, chapter: nextChapter } = target;

    if (isPlayingRef.current && utteranceRef.current) {
      window.speechSynthesis.cancel();
      utteranceRef.current = null;
    }

    if (nextBookIndex !== currentBookIndex) {
      const targetBook = books[nextBookIndex];
      if (targetBook) {
        setCurrentBookIndex(nextBookIndex);
        setCurrentBook(targetBook.name);
        bookSelectionState.current.bookIndex = nextBookIndex;
        bookSelectionState.current.prefix = "";
        bookSelectionState.current.cursor = 0;
      }
    }
    setCurrentChapter(nextChapter);
    setCurrentVersePage(0);
    return true;
  }

  const focusElementByRef = useCallback((ref) => {
    if (ref?.current) {
      ref.current.focus();
      if (typeof ref.current.scrollIntoView === "function") {
        ref.current.scrollIntoView({ block: "nearest" });
      }
    }
  }, []);

  const cycleLanguage = useCallback(() => {
    setCurrentLanguage((prev) => {
      const currentIndex = LANGUAGE_OPTIONS.findIndex(
        (option) => option.value === prev,
      );
      const nextIndex =
        currentIndex === -1
          ? 0
          : (currentIndex + 1) % LANGUAGE_OPTIONS.length;
      return LANGUAGE_OPTIONS[nextIndex].value;
    });
  }, []);

  const cycleVersion = useCallback(() => {
    setCurrentVersion((prev) => {
      const currentIndex = VERSION_OPTIONS.findIndex(
        (option) => option.value === prev,
      );
      const nextIndex =
        currentIndex === -1
          ? 0
          : (currentIndex + 1) % VERSION_OPTIONS.length;
      return VERSION_OPTIONS[nextIndex].value;
    });
  }, []);

  const focusSearchField = useCallback(() => {
    focusElementByRef(searchInputRef);
  }, [focusElementByRef]);

  const focusBookSelectorControl = useCallback(() => {
    focusElementByRef(bookSelectorRef);
  }, [focusElementByRef]);

  const focusChapterSelectorControl = useCallback(() => {
    focusElementByRef(chapterSelectorRef);
  }, [focusElementByRef]);

  const cycleDisplayMode = useCallback(() => {
    setDisplayMode((prev) => {
      const currentIndex = DISPLAY_MODES.indexOf(prev);
      const nextIndex =
        currentIndex === -1
          ? 0
          : (currentIndex + 1) % DISPLAY_MODES.length;
      return DISPLAY_MODES[nextIndex];
    });
  }, []);

  const cycleHighlightMode = useCallback(() => {
    setHighlightMode((prev) => {
      const currentIndex = HIGHLIGHT_MODES.indexOf(prev);
      const nextIndex =
        currentIndex === -1
          ? 0
          : (currentIndex + 1) % HIGHLIGHT_MODES.length;
      return HIGHLIGHT_MODES[nextIndex];
    });
  }, []);

  const toggleVoiceSetting = useCallback(() => {
    setVoiceEnabled((prev) => !prev);
  }, []);

  const toggleSecondaryCompare = useCallback(() => {
    setSecondaryEnabled((prev) => !prev);
  }, []);

  const openHelpModal = useCallback(() => {
    setShowHelp(true);
  }, []);

  const closeHelpModal = useCallback(() => {
    setShowHelp(false);
  }, []);

  const toggleHelpModal = useCallback(() => {
    setShowHelp((prev) => !prev);
  }, []);

  // -----------------------------
  //    TTS Helpers
  // -----------------------------
  function getCurrentSlideTTS() {
    const versesToShow = getDisplayedVerses();
    return versesToShow.map((v) => v.text).join(" ");
  }

  function speakCurrentSlide() {
    if (utteranceRef.current) {
      window.speechSynthesis.cancel();
      utteranceRef.current = null;
    }
    const text = getCurrentSlideTTS();
    if (!text.trim()) return;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = speechRate;
    if (voiceEnabled && selectedVoice && selectedVoice !== "Default") {
      const voice = availableVoices.find((v) => v.name === selectedVoice);
      if (voice) utterance.voice = voice;
    }
    let cumulative = [];
    let sum = 0;
    slideTokens.forEach((t) => {
      cumulative.push(sum);
      sum += t.length;
    });
    utterance.onboundary = (e) => {
      if (e.name === "word") {
        const idx = cumulative.findIndex((c, i) => {
          if (i === cumulative.length - 1) return true;
          return e.charIndex < cumulative[i + 1];
        });
        if (idx !== -1) setCurrentTokenIndex(idx);
      }
    };
    utterance.onend = () => {
      setTimeout(() => moveToNextSlideAndContinue(), 300);
    };
    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  }

  function highlightNextToken(idx) {
    if (!isPlayingRef.current) return;
    if (idx >= slideTokens.length) {
      moveToNextSlideAndContinue();
      return;
    }
    const token = slideTokens[idx].trim();
    if (token === "" || !isNaN(token)) {
      highlightNextToken(idx + 1);
      return;
    }
    setCurrentTokenIndex(idx);
    const delayMs =
      Math.ceil(1000 / (wordsPerMinute / 60)) +
      (/[.,!?;:]/.test(token) ? 300 : 0);
    autoAdvanceRef.current = setTimeout(
      () => highlightNextToken(idx + 1),
      delayMs,
    );
  }

  function moveToNextSlideAndContinue() {
    if (!isPlayingRef.current) return;
    if ((currentVersePage + 1) * versesPerPage < currentVerses.length) {
      setCurrentVersePage((p) => p + 1);
      if (!voiceEnabled) {
        setTimeout(() => highlightNextToken(0), 300);
      }
    } else {
      moveToNextChapter(true);
      setCurrentVersePage(0);
      if (!voiceEnabled) {
        setTimeout(() => highlightNextToken(0), 300);
      }
    }
  }

  function seekBibleIsAudioToVisibleVerse() {
    const audioEl = audioRef.current;
    if (!audioEl || audioTimestamps.length === 0) return;
    const targetVerse = getFirstVisibleVerseNumber();
    if (!targetVerse) return;

    let targetTimestamp = null;
    for (let i = 0; i < audioTimestamps.length; i += 1) {
      const entry = audioTimestamps[i];
      if (entry.verse >= targetVerse) {
        targetTimestamp = entry.timestamp;
        break;
      }
    }
    if (targetTimestamp === null && audioTimestamps.length > 0) {
      targetTimestamp = audioTimestamps[audioTimestamps.length - 1].timestamp;
    }
    if (targetTimestamp === null || Number.isNaN(targetTimestamp)) return;

    const applySeek = () => {
      if (Math.abs(audioEl.currentTime - targetTimestamp) > 0.25) {
        audioEl.currentTime = targetTimestamp;
      }
    };

    if (audioEl.readyState >= 1) {
      applySeek();
    } else {
      const handleLoadedMeta = () => {
        applySeek();
        audioEl.removeEventListener("loadedmetadata", handleLoadedMeta);
      };
      audioEl.addEventListener("loadedmetadata", handleLoadedMeta);
    }
    setCurrentAudioVerse(targetVerse);
  }

  // -----------------------------
  //    Play/Pause / Auto-Advance
  // -----------------------------
  function togglePlayPause() {
    if (
      isBibleIs &&
      (bibleIsMediaType === "audio" || bibleIsMediaType === "audioDrama")
    ) {
      const audioEl = audioRef.current;
      if (!audioEl || audioIsLoading || audioError || !audioData) {
        return;
      }
      if (audioEl.paused) {
        seekBibleIsAudioToVisibleVerse();
        audioEl.play().catch((err) => {
          console.error("Bible.is audio playback failed:", err);
        });
      } else {
        audioEl.pause();
      }
      return;
    }
    if (isBibleIs && bibleIsMediaType === "video") {
      const videoEl = videoRef.current;
      if (!videoEl || videoIsLoading || videoError || !videoData) {
        return;
      }
      if (videoEl.paused) {
        videoEl.play().catch((err) => {
          console.error("Bible.is video playback failed:", err);
        });
      } else {
        videoEl.pause();
      }
      return;
    }
    if (isPlaying) {
      pauseAutoAdvance();
    } else {
      startAutoAdvance();
    }
  }

  function startAutoAdvance() {
    if (!slideTokens || slideTokens.length === 0) return;
    setIsPlaying(true);
    isPlayingRef.current = true;
    if (voiceEnabled) {
      speakCurrentSlide();
    } else if (highlightMode === "word") {
      highlightNextToken(0);
    }
  }

  function pauseAutoAdvance() {
    setIsPlaying(false);
    isPlayingRef.current = false;
    if (autoAdvanceRef.current) {
      clearTimeout(autoAdvanceRef.current);
    }
    if (utteranceRef.current) {
      window.speechSynthesis.cancel();
      utteranceRef.current = null;
    }
  }

  // -----------------------------
  //       WPM Adjustments
  // -----------------------------
  function increaseWPM() {
    setWordsPerMinute((wpm) => wpm + 1);
  }

  function decreaseWPM() {
    setWordsPerMinute((wpm) => (wpm > 1 ? wpm - 1 : 1));
  }

  function onWpmSliderChange(e) {
    const val = parseInt(e.target.value, 10);
    setWordsPerMinute(val);
  }

  // -----------------------------
  //     Font Size Adjustments
  // -----------------------------
  function increaseFontSize() {
    setFontSize((size) => size + 1);
  }

  function decreaseFontSize() {
    setFontSize((size) => (size > 1 ? size - 1 : 1));
  }

  function onFontSizeSliderChange(e) {
    const val = parseInt(e.target.value, 10);
    manualFontOverrideRef.current = true;
    setFontSize(val);
  }

  // -----------------------------
  //        Verses/Slider
  // -----------------------------
  function increaseVersesPerPage() {
    if (displayMode === "full") return;
    if (versesPerPage < 20) {
      setVersesPerPage((v) => v + 1);
      setCurrentVersePage(0);
    }
  }

  function decreaseVersesPerPage() {
    if (displayMode === "full") return;
    if (versesPerPage > 1) {
      setVersesPerPage((v) => v - 1);
      setCurrentVersePage(0);
    }
  }

  function onSliderChange(e) {
    if (displayMode === "full") return;
    const val = parseInt(e.target.value, 10);
    setVersesPerPage(val);
    setCurrentVersePage(0);
  }

  const clearCustomSequence = useCallback(() => {
    setCustomSequenceInput("");
    setCustomSequenceEntries([]);
    setCustomSequenceIndex(0);
  }, []);

  function applyCustomSequenceInput(rawValue) {
    const trimmed = (rawValue || "").trim();
    setCustomSequenceInput(trimmed);
    const parsed = parseCustomSequence(trimmed);
    setCustomSequenceEntries(parsed);
    setCustomSequenceIndex(0);
    if (parsed.length > 0) {
      setShowLandingView(false);
    }
  }

  function handleCustomSequenceKeyDown(e) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    applyCustomSequenceInput(e.target.value);
    e.target.blur();
  }

  // -----------------------------
  //   Book, Chapter Selectors
  // -----------------------------
  function handleBookChange(e) {
    clearCustomSequence();
    const index = parseInt(e.target.value, 10);
    setCurrentBookIndex(index);
    setCurrentBook(books[index].name);
    setCurrentChapter(1);
    setCurrentVersePage(0);
    bookSelectionState.current.bookIndex = index;
    bookSelectionState.current.prefix = "";
    bookSelectionState.current.cursor = 0;
    setShowLandingView(false);
  }

  function handleChapterChange(e) {
    clearCustomSequence();
    const ch = parseInt(e.target.value, 10);
    if (Number.isNaN(ch)) return;

    const currentMeta = books[currentBookIndex];
    if (!currentMeta) return;
    const totalChapters = currentMeta.chapters ?? 1;

    if (ch < 1) {
      if (currentBookIndex > 0) {
        const prevIndex = currentBookIndex - 1;
        const prevBook = books[prevIndex];
        if (prevBook) {
          setCurrentBookIndex(prevIndex);
          setCurrentBook(prevBook.name);
          setCurrentChapter(prevBook.chapters ?? 1);
          setCurrentVersePage(0);
          bookSelectionState.current.bookIndex = prevIndex;
          bookSelectionState.current.prefix = "";
          bookSelectionState.current.cursor = 0;
        }
      } else {
        setCurrentChapter(1);
        setCurrentVersePage(0);
      }
      return;
    }

    if (ch > totalChapters) {
      if (currentBookIndex < books.length - 1) {
        const nextIndex = currentBookIndex + 1;
        const nextBook = books[nextIndex];
        if (nextBook) {
          setCurrentBookIndex(nextIndex);
          setCurrentBook(nextBook.name);
          setCurrentChapter(1);
          setCurrentVersePage(0);
          bookSelectionState.current.bookIndex = nextIndex;
          bookSelectionState.current.prefix = "";
          bookSelectionState.current.cursor = 0;
        }
      } else {
        setCurrentChapter(totalChapters);
        setCurrentVersePage(0);
      }
      return;
    }

    setCurrentChapter(ch);
    setCurrentVersePage(0);
  }

  function handleVersionChange(e) {
    setCurrentVersion(e.target.value);
  }

  function handleLanguageChange(e) {
    setCurrentLanguage(e.target.value);
  }

  const handleLandingBookSelect = useCallback(
    (index) => {
      clearCustomSequence();
      const selectedBook = books[index];
      if (!selectedBook) return;
      const desiredChapter = Math.min(
        Math.max(landingChapterSelections[index] ?? 1, 1),
        selectedBook.chapters ?? 1,
      );
      setCurrentBookIndex(index);
      setCurrentBook(selectedBook.name);
      setCurrentChapter(desiredChapter);
      setCurrentVersePage(0);
      bookSelectionState.current.bookIndex = index;
      bookSelectionState.current.prefix = "";
      bookSelectionState.current.cursor = 0;
      setShowLandingView(false);
    },
    [books, clearCustomSequence, landingChapterSelections],
  );

  const handleLandingChapterChange = useCallback((index, nextChapter) => {
    setLandingChapterSelections((prev) => {
      if (prev[index] === nextChapter) return prev;
      const updated = [...prev];
      updated[index] = nextChapter;
      return updated;
    });
  }, []);

  const handleLandingVersionSelect = useCallback((version) => {
    setCurrentVersion(version);
  }, []);

  const resetLandingQuickState = useCallback(() => {
    landingSelectionState.current = { prefix: "", cursor: 0, matches: [] };
    setLandingHighlightedBookIndex(null);
  }, []);

  const openLandingView = useCallback(() => {
    if (!isBibleIs) return;
    landingQuickBufferRef.current = "";
    setLandingQuickBuffer("");
    resetLandingQuickState();
    updateQuickJumpBuffer("");
    setShowLandingView(true);
  }, [isBibleIs, resetLandingQuickState, updateQuickJumpBuffer]);

  const toggleLandingView = useCallback(() => {
    if (showLandingView) {
      setShowLandingView(false);
      return;
    }
    openLandingView();
  }, [openLandingView, showLandingView]);

  const processLandingQuickInput = useCallback(
    (
      rawValue,
      { cycle = false, keepBuffer = false, reverse = false } = {},
    ) => {
      if (!keepBuffer) {
        landingQuickBufferRef.current = rawValue;
        setLandingQuickBuffer(rawValue);
      }
      const { key, chapter } = parseLandingQuickInput(rawValue);
      if (!key) {
        resetLandingQuickState();
        return;
      }
      const matches = sanitizedBooks.filter((book) =>
        book.key.startsWith(key),
      );
      if (matches.length === 0) {
        resetLandingQuickState();
        return;
      }
      if (cycle) {
        const direction = reverse ? -1 : 1;
        landingSelectionState.current.cursor =
          (landingSelectionState.current.cursor + direction + matches.length) %
          matches.length;
      } else if (landingSelectionState.current.prefix !== key) {
        landingSelectionState.current.cursor = 0;
      }
      landingSelectionState.current.prefix = key;
      landingSelectionState.current.matches = matches;
      const selected = matches[landingSelectionState.current.cursor];
      if (!selected) {
        resetLandingQuickState();
        return;
      }
      setLandingHighlightedBookIndex(selected.index);
      if (chapter && !Number.isNaN(chapter)) {
        const meta = books[selected.index];
        if (meta) {
          const maxChapter = meta.chapters ?? 1;
          handleLandingChapterChange(
            selected.index,
            Math.min(Math.max(chapter, 1), maxChapter),
          );
        }
      }
    },
    [
      books,
      handleLandingChapterChange,
      resetLandingQuickState,
      sanitizedBooks,
    ],
  );

  useEffect(() => {
    if (!showLandingView) {
      landingQuickBufferRef.current = "";
      setLandingQuickBuffer("");
      resetLandingQuickState();
    }
  }, [resetLandingQuickState, showLandingView]);


  // -----------------------------
  //         Searching
  // -----------------------------
  function handleSearchInput(e) {
    const value = e.target.value;
    setSearchQuery(value);

    if (!value) {
      setAutocompleteItems([]);
      return;
    }

    // Naive startsWith matching
    const matches = books.filter((book) =>
      book.name.toLowerCase().startsWith(value.toLowerCase()),
    );
    setAutocompleteItems(matches);
  }

  function applyAutocompleteItem(bookName) {
    setSearchQuery(bookName);
    setAutocompleteItems([]);
  }

  function closeAutocomplete() {
    setAutocompleteItems([]);
  }

  function handleSearchKeyPress(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!searchQuery) return;
      parseAndExecuteSearch(searchQuery);
    }
  }

  function parseAndExecuteSearch(query) {
    const trimmed = (query || "").trim();
    if (!trimmed) return false;
    clearCustomSequence();

    const normalized = trimmed
      .replace(/\bto\b/gi, " ")
      .replace(/\s*-\s*/g, " ")
      .replace(/\s*:\s*/g, ":")
      .replace(/\s+/g, " ")
      .trim();

    const prefixMatch = normalized.match(/^([0-9]?)\s*([A-Za-z]+)/);
    let bookIndex = currentBookIndex;
    let matchedBookName = currentBook;
    let implicitChapter = null;

    if (prefixMatch) {
      const lookupInfo = extractBookLookup(
        prefixMatch[0].replace(/\s+/g, ""),
      );
      if (lookupInfo.key) {
        let matchEntry = null;
        const state = bookSelectionState.current;
        if (
          state.prefix &&
          state.bookIndex !== null &&
          state.bookIndex !== undefined
        ) {
          const stateBook = sanitizedBooks.find(
            (b) => b.index === state.bookIndex,
          );
          if (stateBook && stateBook.key.startsWith(lookupInfo.key)) {
            matchEntry = stateBook;
          }
        }
        if (!matchEntry) {
          matchEntry = sanitizedBooks.find((b) => b.key === lookupInfo.key);
        }
        if (!matchEntry) {
          matchEntry = sanitizedBooks.find((b) =>
            b.key.startsWith(lookupInfo.key),
          );
        }
        if (matchEntry) {
          bookIndex = matchEntry.index;
          matchedBookName = matchEntry.name;
          const matching = sanitizedBooks.filter((b) =>
            b.key.startsWith(lookupInfo.key),
          );
          const cursor = matching.findIndex((b) => b.index === matchEntry.index);
          bookSelectionState.current.prefix = lookupInfo.key;
          bookSelectionState.current.cursor = cursor >= 0 ? cursor : 0;
          bookSelectionState.current.bookIndex = matchEntry.index;
        }
      }
      implicitChapter = lookupInfo.chapter;
    }

    let remainder = prefixMatch
      ? normalized.slice(prefixMatch[0].length).trim()
      : normalized;

    if (!remainder && prefixMatch) {
      remainder = trimmed.slice(prefixMatch[0].length).trim();
    }

    let chapterRaw = null;
    let startRaw = null;
    let endRaw = null;

    if (remainder) {
      const chapterMatch = remainder.match(/^(\d+)/);
      if (!chapterMatch) {
        alert("Invalid chapter.");
        return false;
      }
      chapterRaw = chapterMatch[1];
      remainder = remainder.slice(chapterMatch[0].length).trim();
      if (remainder.startsWith(":")) {
        remainder = remainder.slice(1).trim();
      }
      if (remainder) {
        const normalizedRange = remainder.replace(/[-\s]+/g, " ").trim();
        const parts = normalizedRange.split(" ").filter(Boolean);
        if (parts.length > 0) {
          startRaw = parts[0];
          if (parts.length > 1) {
            endRaw = parts[parts.length - 1];
          }
        }
      }
    }

    const chapter = chapterRaw
      ? parseInt(chapterRaw, 10)
      : implicitChapter !== null
        ? implicitChapter
        : currentChapter;
    if (
      isNaN(chapter) ||
      chapter < 1 ||
      chapter > books[bookIndex].chapters
    ) {
      alert("Invalid chapter.");
      return false;
    }

    let verseStart = null;
    let verseEnd = null;
    if (startRaw) {
      verseStart = parseInt(startRaw, 10);
      if (isNaN(verseStart) || verseStart < 1) {
        alert("Invalid start verse.");
        return false;
      }
    }
    if (endRaw) {
      verseEnd = parseInt(endRaw, 10);
      if (isNaN(verseEnd) || verseEnd < verseStart) {
        alert("Invalid verse range.");
        return false;
      }
    }

    setCurrentBookIndex(bookIndex);
    setCurrentBook(matchedBookName);
    setCurrentChapter(chapter);
    setCurrentVersePage(0);
    if (verseStart !== null) {
      setCurrentVerseRange(verseEnd ? `${verseStart}-${verseEnd}` : `${verseStart}`);
    } else {
      setCurrentVerseRange("");
    }

    // jump after fetch
    setTimeout(() => {
      if (verseStart !== null) jumpToVerse(verseStart, verseEnd);
    }, 600);

    setAutocompleteItems([]);
    return true;
  }

  // Jump to verse or verse range
  function jumpToVerse(verseStart, verseEnd = null) {
    if (!currentVerses || currentVerses.length === 0) return;
    const startIndex = currentVerses.findIndex((v) => v.verse === verseStart);
    if (startIndex === -1) {
      alert("Starting verse not found.");
      return;
    }

    if (verseEnd !== null) {
      const endIndex = currentVerses.findIndex((v) => v.verse === verseEnd);
      if (endIndex === -1) {
        alert("Ending verse not found.");
        return;
      }
      // We'll temporarily set versesPerPage to show that entire range
      const rangeSize = endIndex - startIndex + 1;
      setVersesPerPage(rangeSize);
      setCurrentVersePage(Math.floor(startIndex / rangeSize));
    } else {
      // Just jump to the page that contains verseStart
      setCurrentVersePage(Math.floor(startIndex / versesPerPage));
    }
  }

  // Book selection by letter
  const bookSelectionState = useRef({
    prefix: "",
    cursor: 0,
    bookIndex: initialParams.bookIndex ?? 0,
  });

  function selectBookByPrefixKey(key, cycle = false) {
    clearCustomSequence();
    const normalized = (key || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    if (!normalized) return;
    const matching = sanitizedBooks.filter((b) => b.key.startsWith(normalized));
    if (matching.length === 0) return;

    if (!cycle || bookSelectionState.current.prefix !== normalized) {
      bookSelectionState.current.prefix = normalized;
      bookSelectionState.current.cursor = 0;
    } else {
      bookSelectionState.current.cursor =
        (bookSelectionState.current.cursor + 1) % matching.length;
    }

    const selectedBook = matching[bookSelectionState.current.cursor];
    if (selectedBook) {
      bookSelectionState.current.bookIndex = selectedBook.index;
      setCurrentBookIndex(selectedBook.index);
      setCurrentBook(selectedBook.name);
      setCurrentChapter(1);
      setCurrentVersePage(0);
    }
  }

  // -----------------------------
  //      URL/Sharing
  // -----------------------------
  function generateShareURL() {
    const params = new URLSearchParams();
    params.set("book", currentBookIndex);
    params.set("chapter", currentChapter);
    params.set("version", currentVersion);
    params.set("language", currentLanguage);
    if (customSequenceEntries.length > 0 && customSequenceInput) {
      params.set("custom", customSequenceInput);
    }
    if (currentVerseRange) {
      params.set("verses", currentVerseRange);
    }
    params.set("page", currentVersePage);
    params.set("lines", versesPerPage);
    params.set("wpm", wordsPerMinute);
    params.set("mode", displayMode);
    if (autoPlayNext) {
      params.set("autonext", "1");
    } else {
      params.delete("autonext");
    }
    if (secondaryEnabled) {
      params.set("secondary", "1");
      params.set("secondaryLang", secondaryLanguage);
      params.set("secondaryVersion", secondaryVersion);
    } else {
      params.delete("secondary");
      params.delete("secondaryLang");
      params.delete("secondaryVersion");
    }

    const base = window.location.origin + window.location.pathname;
    return base + "?" + params.toString();
  }

  function updateURL() {
    const newUrl = generateShareURL();
    window.history.replaceState({}, "", newUrl);
  }

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text).catch((err) => {
      console.error("Failed to copy: ", err);
    });
  }

  function handleShare() {
    const url = generateShareURL();
    copyToClipboard(url);
    setShowShareTooltip(true);

    // Hide tooltip after 2 seconds
    setTimeout(() => {
      setShowShareTooltip(false);
    }, 2000);
  }

  // -----------------------------
  //    Handling URL Params
  // -----------------------------
  function handleURLParameters() {
    const raw = location.search;

    if (raw && !raw.includes("=")) {
      const direct = raw.replace(/\+/g, " ").replace(/^\?/, "");
      parseAndExecuteSearch(direct);
      return;
    }

    const params = parseURLParams(raw);

    setCustomSequenceInput(params.customRaw ?? "");
    setCustomSequenceEntries(params.customEntries ?? []);
    setCustomSequenceIndex(
      findCustomSequenceIndex(params.customEntries, {
        bookIndex: params.bookIndex,
        chapter: params.chapter,
        verses: params.verses,
      }),
    );
    setCurrentBookIndex(params.bookIndex);
    setCurrentBook(booksData[params.bookIndex].name);
    setCurrentChapter(params.chapter);
    setCurrentVersion(params.version);
    setCurrentLanguage(params.language);
    setVersesPerPage(params.lines);
    setWordsPerMinute(params.wpm);
    setCurrentVersePage(params.page);
    setCurrentVerseRange(params.verses);
    setDisplayMode(params.mode ?? "default");
    setSecondaryEnabled(params.secondary ?? false);
    setSecondaryLanguage(params.secondaryLang ?? params.language);
    setSecondaryVersion(params.secondaryVersion ?? DEFAULT_VERSION);
    setAutoPlayNext(params.autoNext ?? false);
  }

  // -----------------------------
  //   Controls Visibility
  // -----------------------------
  const toggleControlsVisibility = useCallback(() => {
    const shouldShow =
      topControlsHiddenRef.current || bottomControlsHiddenRef.current;
    const nextHidden = !shouldShow;
    setTopControlsHidden(nextHidden);
    setBottomControlsHidden(nextHidden);
    if (!shouldShow) {
      updateQuickJumpBuffer("");
    }
  }, [updateQuickJumpBuffer]);

  const shouldSkipNavDrag = (target) =>
    Boolean(
      target &&
        typeof target.closest === "function" &&
        target.closest(".br-bottom-meta, button, select, input"),
    );

  const handleNavDragStart = useCallback((event) => {
    if (event.button !== 0) return;
    if (shouldSkipNavDrag(event.target)) return;
    const node = navRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    navDragOffsetRef.current = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    setNavPosition({ left: rect.left, top: rect.top });
    setIsDraggingNav(true);
    event.preventDefault();
  }, []);

  const handleNavTouchStart = useCallback((event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    if (shouldSkipNavDrag(event.target)) return;
    const node = navRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    navDragOffsetRef.current = {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    };
    setNavPosition({ left: rect.left, top: rect.top });
    setIsDraggingNav(true);
    event.preventDefault();
  }, []);

  const hideTopControlsOnly = useCallback(() => {
    setTopControlsHidden(true);
  }, []);

  useEffect(() => {
    const headerLogo = document.querySelector(".header-logo");
    if (!headerLogo) return undefined;
    const previousCursor = headerLogo.style.cursor;
    headerLogo.style.cursor = "pointer";
    const handleToggle = (event) => {
      event.preventDefault();
      toggleControlsVisibility();
    };
    headerLogo.addEventListener("click", handleToggle);
    return () => {
      headerLogo.removeEventListener("click", handleToggle);
      headerLogo.style.cursor = previousCursor;
    };
  }, [toggleControlsVisibility]);

  // -----------------------------
  //    Verse Input Utilities
  // -----------------------------
  function getVerseInputValue() {
    if (currentVerseRange) return currentVerseRange;
    if (!currentVerses || currentVerses.length === 0) return "";
    const startIndex = currentVersePage * versesPerPage;
    const endIndex = Math.min(startIndex + versesPerPage, currentVerses.length);

    const startVerse = currentVerses[startIndex]?.verse;
    const endVerse = currentVerses[endIndex - 1]?.verse;

    if (!startVerse) return "";
    if (startVerse === endVerse) return String(startVerse);
    return `${startVerse}-${endVerse}`;
  }

  // -----------------------------
  //         Render
  // -----------------------------
  const currentTestamentKey = getTestamentKey(currentBook);
  const isAudioMode =
    bibleIsMediaType === "audio" || bibleIsMediaType === "audioDrama";
  const isVideoMode = bibleIsMediaType === "video";

  useLayoutEffect(() => {
    const panel = mediaPanelRef.current;
    const nav = navRef.current;
    const shouldDock =
      isBibleIs &&
      (isAudioMode || isVideoMode) &&
      !showLandingView &&
      !bottomControlsHidden &&
      panel &&
      nav;

    const clearDockPosition = () => {
      if (!panel) return;
      panel.style.removeProperty("left");
      panel.style.removeProperty("top");
      panel.style.removeProperty("bottom");
      panel.style.removeProperty("width");
      panel.style.removeProperty("transform");
    };

    if (!shouldDock) {
      clearDockPosition();
      return undefined;
    }

    let animationFrame = null;
    const syncPanelToNav = () => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }
      animationFrame = window.requestAnimationFrame(() => {
        const navRect = nav.getBoundingClientRect();
        const panelRect = panel.getBoundingClientRect();
        const panelWidth = isVideoMode
          ? Math.min(navRect.width, 760)
          : navRect.width;
        const panelLeft =
          navRect.left + Math.max(0, (navRect.width - panelWidth) / 2);
        panel.style.left = `${panelLeft}px`;
        panel.style.top = `${Math.max(8, navRect.top - panelRect.height - 8)}px`;
        panel.style.bottom = "auto";
        panel.style.width = `${panelWidth}px`;
        panel.style.transform = "translateZ(0)";
        animationFrame = null;
      });
    };

    syncPanelToNav();
    window.addEventListener("resize", syncPanelToNav);
    window.addEventListener("orientationchange", syncPanelToNav);
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(syncPanelToNav)
        : null;
    resizeObserver?.observe(nav);
    resizeObserver?.observe(panel);

    return () => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }
      window.removeEventListener("resize", syncPanelToNav);
      window.removeEventListener("orientationchange", syncPanelToNav);
      resizeObserver?.disconnect();
      clearDockPosition();
    };
  }, [
    bottomControlsHidden,
    isAudioMode,
    isBibleIs,
    isVideoMode,
    navPosition,
    showLandingView,
    viewportWidth,
  ]);

  const audioAvailableForSelection =
    isBibleIs &&
    !!resolveBibleIsFileset(
      currentLanguage,
      currentVersion,
      currentTestamentKey,
      "audio",
    );
  const audioDramaAvailableForSelection =
    isBibleIs &&
    !!resolveBibleIsFileset(
      currentLanguage,
      currentVersion,
      currentTestamentKey,
      "audioDrama",
    );
  const videoAvailableForSelection =
    isBibleIs &&
    !!resolveBibleIsFileset(
      currentLanguage,
      currentVersion,
      currentTestamentKey,
      "video",
    );
  const availableMediaModes = useMemo(() => {
    const modes = ["text"];
    if (audioAvailableForSelection) modes.push("audio");
    if (audioDramaAvailableForSelection) modes.push("audioDrama");
    if (videoAvailableForSelection) modes.push("video");
    return modes;
  }, [
    audioAvailableForSelection,
    audioDramaAvailableForSelection,
    videoAvailableForSelection,
  ]);

  const secondaryVerseMap = useMemo(() => {
    const map = new Map();
    secondaryVerses.forEach((verse) => {
      map.set(verse.verse, verse.text);
    });
    return map;
  }, [secondaryVerses]);

  const bibleIsModeIcon =
    bibleIsMediaType === "audioDrama"
      ? "\u{1F3AD}"
      : bibleIsMediaType === "audio"
          ? "\u{1F50A}"
          : bibleIsMediaType === "video"
              ? "\u{1F3AC}"
              : "\u{1F507}";
  const bibleIsModeTitle = (() => {
    if (availableMediaModes.length <= 1) {
      return "Media unavailable — Text Only";
    }
    switch (bibleIsMediaType) {
      case "audio":
        return "Audio + Text";
      case "audioDrama":
        return "Audio Drama + Text";
      case "video":
        return "Video + Text";
      default:
        return "Text Only";
    }
  })();
  const nonTextMediaIsPlaying = isAudioMode ? audioIsPlaying : videoIsPlaying;
  const nonTextMediaDisabled =
    isBibleIs &&
    bibleIsMediaType !== "text" &&
    (isAudioMode
      ? audioIsLoading || !!audioError || !audioData
      : isVideoMode
          ? videoIsLoading || !!videoError || !videoData
          : false);

  const typingIndicatorValue = showLandingView
    ? landingQuickBuffer
    : quickJumpBuffer;

  const navChapterMax = Math.max(books[currentBookIndex]?.chapters ?? 1, 1);
  const navStyle =
    navPosition.left !== null && navPosition.top !== null
      ? {
          left: `${navPosition.left}px`,
          top: `${navPosition.top}px`,
          bottom: "auto",
          transform: "translate3d(0,0,0)",
        }
      : undefined;
  const navClassName = [
    bottomControlsHidden ? "br-hidden" : "",
    isDraggingNav ? "br-bottom-nav--dragging" : "",
  ]
    .filter(Boolean)
    .join(" ");

  function cycleBibleIsMediaMode() {
    if (!isBibleIs || availableMediaModes.length <= 1) return;
    const currentIndex = availableMediaModes.indexOf(bibleIsMediaType);
    const nextIndex = (currentIndex + 1) % availableMediaModes.length;
    setBibleIsMediaType(availableMediaModes[nextIndex]);
  }

  return (
    <div
      className="br-bible-reader-root" // Updated className
      style={
        isBibleIs
          ? {
              boxSizing: "border-box",
              height: "calc(100vh - var(--site-header-height, 72px))",
              minHeight: "0",
              paddingTop: "var(--site-header-height, 72px)",
            }
          : undefined
      }
      // Disable context menu (like your original code)
      onContextMenu={(e) => {
        e.preventDefault();
        return false;
      }}
    >
      <div id="br-bible-container">
        {" "}
        {/* Updated id */}
        <SEO {...seoConfig} />
        {showLandingView && (
          <div
            className="br-landing-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Choose a Bible book"
          >
            <div className="br-version-tile-grid">
              {VERSION_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  className={`br-version-tile ${
                    option.value === currentVersion ? "active" : ""
                  }`}
                  type="button"
                  onClick={() => handleLandingVersionSelect(option.value)}
                >
                  <span className="br-version-label">{option.value}</span>
                </button>
              ))}
            </div>
            <div className="br-book-tile-section">
              <h2>Old Testament</h2>
              <div className="br-book-tile-grid">
                {oldTestamentBooks.map((book, index) => {
                  const absoluteIndex = index;
                  const sliderValue = landingChapterSelections[absoluteIndex] ?? 1;
                  const sliderId = `br-book-slider-${absoluteIndex}`;
                  const tileClass =
                    landingHighlightedBookIndex === absoluteIndex
                      ? "br-book-tile active"
                      : "br-book-tile";
                  return (
                    <div key={book.name} className={tileClass}>
                      <button
                        type="button"
                        className="br-book-select"
                        onClick={() => handleLandingBookSelect(absoluteIndex)}
                        onFocus={() => setLandingHighlightedBookIndex(absoluteIndex)}
                        onMouseEnter={() =>
                          setLandingHighlightedBookIndex(absoluteIndex)
                        }
                      >
                        <span className="br-book-name">
                          {book.name}
                          {book.chapters > 1 && (
                            <span className="br-book-chapter-tag">
                              {sliderValue}
                            </span>
                          )}
                        </span>
                      </button>
                      <div className="br-book-slider">
                        <input
                          id={sliderId}
                          type="range"
                          min="1"
                          max={book.chapters || 1}
                          value={sliderValue}
                          aria-label={`${book.name} chapter`}
                          onChange={(e) =>
                            handleLandingChapterChange(
                              absoluteIndex,
                              parseInt(e.target.value, 10),
                            )
                          }
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="br-book-tile-section">
              <h2>New Testament</h2>
              <div className="br-book-tile-grid">
                {newTestamentBooks.map((book, index) => {
                  const absoluteIndex = index + OLD_TESTAMENT_BOOK_COUNT;
                  const sliderValue = landingChapterSelections[absoluteIndex] ?? 1;
                  const sliderId = `br-book-slider-${absoluteIndex}`;
                  const tileClass =
                    landingHighlightedBookIndex === absoluteIndex
                      ? "br-book-tile active"
                      : "br-book-tile";
                  return (
                    <div key={book.name} className={tileClass}>
                      <button
                        type="button"
                        className="br-book-select"
                        onClick={() => handleLandingBookSelect(absoluteIndex)}
                        onFocus={() => setLandingHighlightedBookIndex(absoluteIndex)}
                        onMouseEnter={() =>
                          setLandingHighlightedBookIndex(absoluteIndex)
                        }
                      >
                        <span className="br-book-name">
                          {book.name}
                          {book.chapters > 1 && (
                            <span className="br-book-chapter-tag">
                              {sliderValue}
                            </span>
                          )}
                        </span>
                      </button>
                      <div className="br-book-slider">
                        <input
                          id={sliderId}
                          type="range"
                          min="1"
                          max={book.chapters || 1}
                          value={sliderValue}
                          aria-label={`${book.name} chapter`}
                          onChange={(e) =>
                            handleLandingChapterChange(
                              absoluteIndex,
                              parseInt(e.target.value, 10),
                            )
                          }
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        <div className="br-reader-shell" aria-hidden={showLandingView}>
        {/* Title */}
        <div id="br-title">
          {" "}
          {/* Updated id */}
          {currentBook} {currentChapter},{" "}
          {currentVerses.length > 0 ? (
            <>Verses {getVerseInputValue() || "1"}</>
          ) : (
            "Loading..."
          )}
        </div>
        {/* Controls */}
        <div id="br-controls" className={topControlsHidden ? "br-hidden" : ""}>
          {" "}
          {/* Updated id and className */}
          <div className="br-controls-group">
            {" "}
            {/* Updated className */}
            {/* Search Input with Autocomplete */}
            <div style={{ position: "relative", width: "280px" }}>
              <input
                type="text"
                id="br-custom-input"
                placeholder="Custom sequence (e.g., Mark 1 3-6, Matthew 4 2-8)"
                autoComplete="off"
                value={customSequenceInput}
                onChange={(e) => setCustomSequenceInput(e.target.value)}
                onBlur={(e) => applyCustomSequenceInput(e.target.value)}
                onKeyDown={handleCustomSequenceKeyDown}
                aria-label="Custom verse sequence"
              />
            </div>
            <div style={{ position: "relative", width: "200px" }}>
              <input
                type="text"
                id="br-search-input" // Updated id
                placeholder="Search Book Chapter Verse"
                autoComplete="off"
                value={searchQuery}
                onChange={handleSearchInput}
                onKeyPress={handleSearchKeyPress}
                ref={searchInputRef}
              />
              {/* Autocomplete dropdown */}
              {autocompleteItems.length > 0 && (
                <div
                  id="br-autocomplete-list"
                  className="br-autocomplete-items"
                >
                  {" "}
                  {/* Updated id and className */}
                  {autocompleteItems.map((item, idx) => (
                    <div
                      key={idx}
                      className="br-autocomplete-item" // Updated className
                      onClick={() => applyAutocompleteItem(item.name)}
                    >
                      <strong>
                        {item.name.substring(0, searchQuery.length)}
                      </strong>
                      {item.name.substring(searchQuery.length)}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Language Selector */}
            <select
              id="br-language-selector" // Updated id
              value={currentLanguage}
              onChange={handleLanguageChange}
              ref={languageSelectorRef}
            >
              {LANGUAGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {/* Version Selector */}
            <select
              id="br-version-selector" // Updated id
              value={currentVersion}
              onChange={handleVersionChange}
              ref={versionSelectorRef}
            >
              {VERSION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {/* Book Selector */}
            <select
              id="br-book-selector" // Updated id
              value={currentBookIndex}
              onChange={handleBookChange}
              ref={bookSelectorRef}
            >
              {books.map((b, i) => (
                <option key={b.name} value={i}>
                  {b.name}
                </option>
              ))}
            </select>
            {/* Chapter Selector */}
            <select
              id="br-chapter-selector" // Updated id
              value={currentChapter}
              onChange={handleChapterChange}
              ref={chapterSelectorRef}
            >
              {Array.from(
                { length: books[currentBookIndex].chapters },
                (_, i) => i + 1,
              ).map((ch) => (
                <option key={ch} value={ch}>
                  {ch}
                </option>
              ))}
            </select>
            {/* Display Mode Selector */}
            <select
              id="br-display-mode-selector"
              value={displayMode}
              onChange={(e) => setDisplayMode(e.target.value)}
            >
              <option value="default">Default Mode</option>
              <option value="slide">Slide Mode</option>
              <option value="full">Full Chapter Mode</option>
            </select>
            {isBibleIs && !showLandingView && (
              <button
                type="button"
                onClick={openLandingView}
                aria-label="Back to Bible tiles"
                title="Back to Bible tiles (Ctrl/Cmd + Alt/Shift + O)"
              >
                Tiles
              </button>
            )}
            <button
              type="button"
              className={autoPlayNext ? "is-active" : ""}
              onClick={() => setAutoPlayNext((prev) => !prev)}
              title="Auto play next chapter"
              aria-pressed={autoPlayNext}
            >
              Auto Next
            </button>
            <label
              htmlFor="br-secondary-toggle"
              style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
            >
              <input
                id="br-secondary-toggle"
                type="checkbox"
                checked={secondaryEnabled}
                onChange={(e) => setSecondaryEnabled(e.target.checked)}
              />
              Compare Version
            </label>
            {secondaryEnabled && (
              <div
                className="br-secondary-controls"
                style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}
              >
                <select
                  id="br-secondary-language"
                  value={secondaryLanguage}
                  onChange={(e) => setSecondaryLanguage(e.target.value)}
                >
                  <option value="en">English</option>
                  <option value="ta">Tamil</option>
                  <option value="te">Telugu</option>
                  <option value="hi">Hindi</option>
                  <option value="ml">Malayalam</option>
                </select>
                <select
                  id="br-secondary-version"
                  value={secondaryVersion}
                  onChange={(e) => setSecondaryVersion(e.target.value)}
                >
                  <option value="KJV">KJV</option>
                  <option value="NKJV">NKJV</option>
                  <option value="NIV">NIV</option>
                  <option value="ESV">ESV</option>
                  <option value="WEB">WEB</option>
                  <option value="RSV">RSV (Catholic)</option>
                  <option value="ICB">ICB (Kids)</option>
                  <option value="AMP">AMP (Study)</option>
                  <option value="CSB">CSB</option>
                  <option value="NLT">NLT</option>
                </select>
              </div>
            )}
            {/* Voice Selection Dropdown */}
            <select
              id="br-voice-selector"
              value={selectedVoice}
              onChange={(e) => setSelectedVoice(e.target.value)}
            >
              <option value="Default">Default Voice</option>
              {availableVoices.map((voice, idx) => (
                <option key={idx} value={voice.name}>
                  {voice.name} ({voice.lang})
                </option>
              ))}
            </select>
            {/* Share Button with Tooltip */}
            <div className="br-tooltip">
              {" "}
              {/* Updated className */}
              <button id="br-share-btn" onClick={handleShare}>
                {" "}
                {/* Updated id */}
                Share
              </button>
              <span
                className="br-tooltiptext" // Updated className
                id="br-share-tooltip" // Updated id
                style={{
                  visibility: showShareTooltip ? "visible" : "hidden",
                  opacity: showShareTooltip ? "1" : "0",
                }}
              >
                URL Copied!
              </span>
            </div>
            {/* Help Button */}
            <button id="br-help-btn" onClick={toggleHelpModal}>
              {" "}
              {/* Updated id */}?
            </button>
            <button
              type="button"
              className="br-close-controls"
              onClick={hideTopControlsOnly}
              aria-label="Hide top controls"
              title="Hide top controls"
            >
              ✕
            </button>
          </div>
          <div className="br-controls-group">
            {" "}
            {/* Updated className */}
            <input
              type="text"
              id="br-verse-input" // Updated id
              placeholder="Verse(s) (e.g., 1)"
              value={getVerseInputValue()}
              readOnly
              style={{ cursor: "default" }}
            />
            {/* Play/Pause Button removed from here */}
            <span id="br-playback-rate-display">Speed</span>{" "}
            <div id="br-wpm-slider-container">
              <label htmlFor="br-wpm-slider">Text:</label>
              <input
                type="range"
                id="br-wpm-slider"
                min="50"
                max="300"
                value={wordsPerMinute}
                onChange={onWpmSliderChange}
              />
              <span id="br-wpm-value">{wordsPerMinute} WPM</span>
            </div>
            <div id="br-audio-speed-container">
              <label htmlFor="br-audio-speed">Audio:</label>
              <input
                type="range"
                id="br-audio-speed"
                min="0.5"
                max="2"
                step="0.05"
                value={speechRate}
                onChange={(e) => setSpeechRate(parseFloat(e.target.value))}
              />
              <span id="br-audio-speed-value">{speechRate.toFixed(2)}x</span>
            </div>
            <div id="br-highlight-color-container">
              <label htmlFor="br-highlight-color">Highlight:</label>
              <input
                type="color"
                id="br-highlight-color"
                value={highlightColor}
                onChange={(e) => setHighlightColor(e.target.value)}
              />
            </div>
            <div id="br-highlight-mode-container">
              <label htmlFor="br-highlight-mode">Highlight:</label>
              <select
                id="br-highlight-mode"
                value={highlightMode}
                onChange={(e) => setHighlightMode(e.target.value)}
              >
                <option value="verse">Highlight Verse</option>
                <option value="word">Highlight Word</option>
                <option value="none">No Highlight</option>
              </select>
            </div>
            <div id="br-voice-toggle-container">
              <label htmlFor="br-voice-toggle">Voice:</label>
              <input
                type="checkbox"
                id="br-voice-toggle"
                checked={voiceEnabled}
                onChange={() => setVoiceEnabled(!voiceEnabled)}
              />
            </div>
            {/* Slider for # of Verses per Slide */}
            <div id="br-slider-container">
              {" "}
              {/* Updated id */}
              <label htmlFor="br-lines-slider">Lines:</label>{" "}
              {/* Updated label */}
              <input
                type="range"
                id="br-lines-slider" // Updated id
                min="1"
                max="20"
                value={versesPerPage}
                onChange={onSliderChange}
                disabled={displayMode === "full"}
              />
              <span id="br-slider-value">{versesPerPage}</span>{" "}
              {/* Updated id */}
            </div>
            {/* Font size slider */}
            <div id="br-fontsize-container">
              <label htmlFor="br-fontsize-slider">Font Size:</label>
              <input
                type="range"
                id="br-fontsize-slider"
                min="12"
                max={fontSliderMax}
                value={fontSize}
                onChange={onFontSizeSliderChange}
              />
              <span id="br-fontsize-value">{fontSize}</span>
            </div>
          </div>
        </div>
        {/* Content Box */}
        <div
          id="br-content-box"
          ref={contentBoxRef}
          style={{ "--br-mobile-font-scale": mobileFontScale }}
        >
          {" "}
          {/* Updated id */}
          <div
            id="br-verses-display"
            ref={verseTouchTargetRef}
            className="br-responsive-text"
            style={{
              fontSize: `${
                viewportWidth <= 640
                  ? Math.max(fontSize * mobileFontScale * 0.45, 6)
                  : Math.max(fontSize * mobileFontScale * 0.8, 9)
              }px`,
            }}
          >
            {" "}
            {/* Updated id and className */}
            {renderContentArea()}
          </div>
        </div>
        {secondaryEnabled && secondaryError && (
          <div className="br-no-verses" style={{ marginTop: "0.5rem" }}>
            {secondaryError}
          </div>
        )}
        {isBibleIs && bibleIsMediaType !== "text" && (
          <div
            ref={mediaPanelRef}
            className={`br-audio-panel ${
              isAudioMode ? "br-audio-panel--docked" : "br-audio-panel--video"
            }`}
          >
            {isAudioMode && (
              <>
                {audioIsLoading && (
                  <div className="br-no-verses">Loading audio...</div>
                )}
                {!audioIsLoading && audioError && (
                  <div className="br-no-verses">{audioError}</div>
                )}
                {!audioIsLoading && !audioError && audioData && (
                  <div className="br-audio-meta">
                    <img
                      className="br-audio-logo"
                      src={audioData.thumbnail || "/logo192.png"}
                      alt="Faith Comes by Hearing Bible audio"
                    />
                    <div className="br-audio-copy">
                      <div className="br-audio-title">
                        {currentBook} {currentChapter}{" "}
                        {bibleIsMediaType === "audioDrama"
                          ? "Audio Drama"
                          : "Audio"}
                      </div>
                      {audioData.duration && (
                        <div className="br-audio-duration">
                          Duration: {formatSeconds(audioData.duration)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <audio
                  id="br-audio-player"
                  ref={audioRef}
                  className="br-audio-element"
                  controls
                  preload="none"
                />
              </>
            )}
            {isVideoMode && (
              <>
                {videoIsLoading && (
                  <div className="br-no-verses">Loading video...</div>
                )}
                {!videoIsLoading && videoError && (
                  <div className="br-no-verses">{videoError}</div>
                )}
                {!videoIsLoading && !videoError && videoData && (
                  <div
                    className="br-audio-meta"
                    style={{ display: "flex", gap: "1rem", alignItems: "center" }}
                  >
                    {videoData.thumbnail && (
                      <img
                        src={videoData.thumbnail}
                        alt="Video artwork"
                        style={{
                          width: "120px",
                          height: "68px",
                          objectFit: "cover",
                          borderRadius: "6px",
                        }}
                      />
                    )}
                    <div>
                      <div
                        className="br-audio-title"
                        style={{ fontWeight: "bold" }}
                      >
                        {currentBook} {currentChapter} Video
                      </div>
                      {videoData.duration && (
                        <div className="br-audio-duration">
                          Duration: {formatSeconds(videoData.duration)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <video
                  id="br-video-player"
                  ref={videoRef}
                  className="br-video-element"
                  controls
                  playsInline
                  preload="metadata"
                />
              </>
            )}
          </div>
        )}
        
        {/* *** Bottom Nav Bar *** */}
        {!showLandingView && (
          <div
            id="br-bottom-nav"
            ref={navRef}
            className={navClassName || undefined}
            style={navStyle}
            onMouseDown={handleNavDragStart}
            onTouchStart={handleNavTouchStart}
          >
            <div className="br-bottom-button-cluster">
              <button
                id="br-up-btn" // Keep ID for onClick
                className="br-nav-action-btn" // New class
                onClick={moveToPrevChapter}
                title="Previous Chapter (ArrowUp)"
              >
                <FaStepBackward />
              </button>
              <button
                id="br-prev-btn" // Keep ID for onClick
                className="br-nav-action-btn" // New class
                onClick={handlePrev}
                title="Previous Slide (ArrowLeft)"
              >
                <FaArrowLeft />
              </button>

              {isBibleIs && (
                <button
                  id="br-audio-toggle-btn"
                  className="br-nav-action-btn"
                  onClick={cycleBibleIsMediaMode}
                  title={`${bibleIsModeTitle} — click to cycle`}
                  disabled={availableMediaModes.length <= 1}
                  style={{
                    opacity:
                      availableMediaModes.length <= 1 || bibleIsMediaType === "text"
                        ? 0.6
                        : 1,
                  }}
                >
                  {bibleIsModeIcon}
                </button>
              )}

              {/* Moved from top controls */}
              <button
                id="br-play-pause-main-btn" // New ID
                className="br-nav-action-btn br-play-pause-main" // New classes
                onClick={togglePlayPause}
                title="Play/Pause (Space)"
                disabled={nonTextMediaDisabled}
              >
                {isBibleIs && bibleIsMediaType !== "text"
                  ? nonTextMediaIsPlaying
                    ? <FaPause />
                    : <FaPlay />
                  : isPlaying
                    ? <FaPause />
                    : <FaPlay />}
              </button>

              <button
                id="br-next-btn" // Keep ID for onClick
                className="br-nav-action-btn" // New class
                onClick={handleNext}
                title="Next Slide (ArrowRight)"
              >
                <FaArrowRight />
              </button>
              <button
                id="br-down-btn" // Keep ID for onClick
                className="br-nav-action-btn" // New class
                onClick={moveToNextChapter}
                title="Next Chapter (ArrowDown)"
              >
                <FaStepForward />
              </button>
              {isBibleIs && (
                <button
                  id="br-tiles-btn"
                  className="br-nav-action-btn"
                  onClick={openLandingView}
                  title="Back to Bible tiles (Ctrl/Cmd + Alt/Shift + O)"
                  aria-label="Back to Bible tiles"
                >
                  <FaTh />
                </button>
              )}
              <button
                type="button"
                className="br-nav-action-btn br-bottom-close-btn br-bottom-meta-close"
                aria-label="Close navigation panel"
                onClick={() => setBottomControlsHidden(true)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="br-bottom-meta">
              <label
                className="br-bottom-selector"
                htmlFor="br-bottom-version-select"
              >
                <FaLayerGroup aria-hidden="true" />
                <select
                  id="br-bottom-version-select"
                  value={currentVersion}
                  onChange={handleVersionChange}
                  aria-label="Select version"
                >
                  {VERSION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="br-bottom-selector" htmlFor="br-bottom-book-select">
                <FaBookReader aria-hidden="true" />
                <select
                  id="br-bottom-book-select"
                  value={currentBookIndex}
                  onChange={handleBookChange}
                  aria-label="Select book"
                >
                  {books.map((b, i) => (
                    <option key={b.name} value={i}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </label>
              <label
                className="br-bottom-selector br-chapter-slider"
                htmlFor="br-bottom-chapter-slider"
              >
                <FaGripLines aria-hidden="true" />
                <input
                  type="range"
                  id="br-bottom-chapter-slider"
                  min="1"
                  max={navChapterMax}
                  value={currentChapter}
                  onChange={handleChapterChange}
                  aria-label="Select chapter"
                />
                <span className="br-chapter-value">{currentChapter}</span>
              </label>
            </div>
          </div>
        )}

        {/* Help Modal */}
        {showHelp && (
          <div id="br-help-modal" className="br-help-modal">
            {" "}
            {/* Updated id and className */}
            <div id="br-help-modal-content" className="br-help-modal-content">
              {" "}
              {/* Updated id and className */}
              <span
                id="br-close-help"
                className="br-close-help"
                onClick={closeHelpModal}
              >
                {" "}
                {/* Updated id and className */}
                &times;
              </span>
              <h2>Help &amp; Keyboard Shortcuts</h2>
              <p>
                Welcome to the Bible Reader application! Below are the keyboard
                shortcuts, quick navigation tips, and touch gestures:
              </p>
              <h3>Keyboard Shortcuts:</h3>
              <ul>
                <li>
                  <strong>F1</strong>: Open this help panel.
                </li>
                <li>
                  <strong>Ctrl</strong>: Toggle top and bottom controls.
                </li>
                <li>
                  <strong>Space</strong>: Play or pause reading / Bible.is media (audio or video).
                </li>
                <li>
                  <strong>Arrow Left / Arrow Right</strong>: Previous / next slide.
                </li>
                <li>
                  <strong>Arrow Up / Arrow Down</strong>: Previous / next chapter.
                </li>
                <li>
                  <strong>Ctrl/Cmd + Alt/Shift + D</strong>: Cycle display modes (Default → Slide → Full).
                </li>
              <li>
                <strong>Ctrl/Cmd + Alt/Shift + H</strong>: Cycle highlight modes (Verse → Word → None).
              </li>
              <li>
                <strong>Ctrl/Cmd + Alt/Shift + L</strong>: Cycle through available languages.
              </li>
              <li>
                <strong>Ctrl/Cmd + Alt/Shift + G</strong>: Cycle through translations/versions.
              </li>
              <li>
                <strong>Ctrl/Cmd + Alt/Shift + F</strong>: Focus the search bar instantly.
              </li>
              <li>
                <strong>Ctrl/Cmd + Alt/Shift + B</strong>: Focus the book selector.
              </li>
              <li>
                <strong>Ctrl/Cmd + Alt/Shift + J</strong>: Focus the chapter selector.
              </li>
              <li>
                <strong>Ctrl/Cmd + Alt/Shift + V</strong>: Toggle voice narration for slide playback.
              </li>
                <li>
                  <strong>Ctrl/Cmd + Alt/Shift + C</strong>: Toggle compare/secondary translation.
                </li>
                <li>
                  <strong>Ctrl/Cmd + Alt/Shift + T</strong>: Toggle top and bottom controls.
                </li>
                {isBibleIs && (
                  <li>
                    <strong>Ctrl/Cmd + Alt/Shift + M</strong>: Cycle Bible.is modes (Text → Audio → Audio Drama → Video).
                  </li>
                )}
                <li>
                  <strong>Ctrl/Cmd + Alt/Shift + ]</strong> /{" "}
                  <strong>Ctrl/Cmd + Alt/Shift + [</strong>: Increase / decrease verses per slide.
                </li>
                <li>
                  <strong>Ctrl/Cmd + Alt/Shift + .</strong> /{" "}
                  <strong>Ctrl/Cmd + Alt/Shift + ,</strong>: Increase / decrease font size.
                </li>
                <li>
                  <strong>Ctrl/Cmd + Alt/Shift + S</strong>: Copy a shareable link of the current view.
                </li>
                {isBibleIs && (
                  <li>
                    <strong>Ctrl/Cmd + Alt/Shift + O</strong>: Open the Bible.is tiles view.
                  </li>
                )}
              </ul>
              <h3>Quick Jump Input:</h3>
              <ul>
                <li>
                  Type book codes with optional chapter/verse (e.g.,{" "}
                  <code>m 8 5 8</code> or <code>ps 23</code>). Letters and
                  numbers are reserved for this quick navigation.
                </li>
                <li>
                  <strong>Tab</strong>: Cycle through books matching the typed
                  prefix.
                </li>
                <li>
                  <strong>Enter</strong>: Load the passage shown in the quick
                  jump indicator.
                </li>
                <li>
                  <strong>Backspace</strong>: Step back through your quick jump
                  input.
                </li>
                <li>
                  <strong>Escape</strong>: Clear the quick jump buffer.
                </li>
              </ul>
              <h3>Touch &amp; Mobile:</h3>
              <ul>
                <li>Swipe left/right on verses to move between slides.</li>
                <li>Swipe up/down to move between chapters.</li>
                <li>Tap the speaker icon in the bottom bar to toggle Bible.is media (audio / video) with text.</li>
                <li>Use the bottom toolbar buttons for play, chapter jumps, and media mode.</li>
              </ul>
              <h3>How to Use:</h3>
              <ul>
                <li>
                  <strong>Search:</strong> Use the search box to find specific
                  passages (e.g., “Genesis 1 1-5”).
                </li>
                <li>
                  <strong>Play/Pause:</strong> Click the play button or press
                  space bar to auto-advance verses.
                </li>
                <li>
                  <strong>Share:</strong> Click “Share” (or press Ctrl/Cmd + Alt/Shift + S)
                  to copy a URL containing your current reading state.
                </li>
                <li>
                  <strong>Toggle Controls:</strong> Press Ctrl to
                  show or hide the top controls.
                </li>
              </ul>
              <p>Enjoy your reading!</p>
            </div>
          </div>
        )}
        </div>
      </div>
      {!isBibleIs && (
        <>
          <div className="random-yt-spacer" aria-hidden="true" />
          <div style={{ marginTop: "48px" }}>
            <RandomYouTubePlayer title="Bible - Featured Video" deferUntilScroll={150} />
          </div>
        </>
      )}
      <div id="bible-watermark" className="global-watermark global-watermark--bible" aria-hidden="true">
        <span>{GLOBAL_WATERMARK_TEXT}</span>
      </div>
      {typingIndicatorValue && (
        <div className="br-typing-indicator">
          {showLandingView ? typingIndicatorValue : `Quick Jump: ${typingIndicatorValue}`}
        </div>
      )}
    </div>
  );
}

export default function BibleReader(props) {
  return <BibleReaderMain {...props} />;
}
